// src/services/aeo/clientReview.ts
//
// The Client Approval layer: the seam between "Oura recommended it" and
// "ourteam did it". Staff turn tracker tasks into a PACK (a document with
// decisions inside it), the merchant answers inside their own Oura login, and
// every answer lands back on the task. One THREAD per item carries the back
// and forth; the item's STATUS moves only when someone decides. Those are two
// different things and this file keeps them apart on purpose.
//
// Principle, enforced here rather than in copy: Oura prepares, ourteam curates,
// the client decides, Oura records. Nothing reaches the merchant until a
// staffer publishes it.
//
// Sits ON TOP of what exists: the merchant is an `oura-client` user in a
// workspace that owns one `aeo_businesses` row (company_id), decisions join to
// `aeo_tracker_activity` tasks, and the shell, nav and primitives are the
// platform's own. Nothing here replaces a current Oura surface.
//
// BE contract (proposed, for Ipul). Every function is shaped as the endpoint
// it will call, so the FE does not change when it ships:
//
//   Staff
//   GET    /businesses/{id}/task-approvals                 → ApprovalOverride[]
//   PUT    /businesses/{id}/task-approvals/{taskId}        → ApprovalOverride
//   GET    /businesses/{id}/packs                          → Pack[]
//   POST   /businesses/{id}/packs                          → Pack           (draft)
//   PATCH  /packs/{id}                                     → Pack
//   POST   /packs/{id}/publish                             → Pack           (client notified)
//   POST   /businesses/{id}/client-users                   → { email }      (creates the login, issues oura-client)
//   GET    /businesses/{id}/call-requests                  → CallRequest[]
//   PATCH  /call-requests/{id}                             → CallRequest
//
//   Merchant (scoped by the login; the BE already has client_scope_domain)
//   GET    /businesses/mine                                → MyBusiness      (already live)
//   GET    /businesses/mine/client-page                    → ClientPagePayload
//   POST   /items/{id}/decide                              → PackItem
//   POST   /businesses/mine/call-requests                  → CallRequest
//   POST   /businesses/mine/reports/{id}/read              → report read state (C-BE-1)
//   POST   /businesses/mine/reports/read-all               → {marked, unreadReports} (C-BE-1)
//   POST   /businesses/mine/documents/{id}/read            → {id, readAt, unreadDocuments} (ourteam-backend#6108)
//
//   Both
//   GET    /businesses/{id}/threads                        → Thread[]
//   POST   /threads/{id}/messages                          → Thread          (other side notified)
//   POST   /threads/{id}/resolve                           → Thread
//   POST   /items/{id}/accept-edit                         → PackItem        (staff accepts a suggested edit)
//   GET    /notifications?audience=client|staff            → Notification[]
//   POST   /notifications/{id}/read                        → Notification
//
// With VITE_ENABLE_OURA_LIVE_API unset everything serves the in-memory store
// at the bottom of this file, persisted to localStorage so a page reload keeps
// a demo going. The loop closes end to end in one browser: publish on the
// staff tab, switch the dev role to client, answer, switch back.

import axios from "axios";
import { sampleDeliverables } from "./deliveryMock";
import type { Deliverable as DeliveryDeliverable } from "./deliveryTypes";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import type { ChangeRequestPatchIn, DocsErrorCode, DocsResult, MerchantDocument, RequestStatus } from "./clientDocsTypes";
import type { QuestionKind } from "./clientDocsWriteTypes";
import { isDocsSample, readDocsError } from "./clientDocs";
import { DOCS_ERROR_MESSAGE, docsDevMode, docsFailure, docsMockDelayMs, mockApprovedReportLinks, mockMerchantDocuments, onDocsNotice } from "./clientDocsMock";
import { lineLabel } from "./lineWords";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;
// Its own flag, separate from VITE_ENABLE_OURA_LIVE_API: the rest of Oura is
// live on staging while these endpoints are still being built (epic
// ourteam-backend#4735). Off means the in-memory store below. T16 flips it.
const useFixtures = import.meta.env.VITE_ENABLE_OURA_CLIENT_REVIEW_LIVE !== "true";

/** True once the approval-layer endpoints are live. Until then the UI runs on the mock, and production hides it. */
export const isClientReviewLive = !useFixtures;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * What `POST /businesses/{id}/uploads` returns (#3674 / backend T14). `fileKey`
 * is the durable handle the pack, decision and thread rows store; `url` is a
 * presigned link for showing it now.
 */
export interface UploadedFile { fileKey: string; url: string; name: string; size: number }

/** PDF, PNG, JPEG or WebP, up to 20 MB — the server's own limits. */
export const UPLOAD_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
export const UPLOAD_MAX_BYTES = 20 * 1024 * 1024;

/**
 * `POST /businesses/{id}/client-users` (#3671 / backend T1).
 *
 * The 409 is an expected answer, not a failure: the email already belongs to a
 * user in a different workspace, which is something the staffer has to resolve,
 * so it comes back as a result rather than a throw.
 */
export type CreateClientUserResult =
    | { ok: true; email: string }
    | { ok: false; kind: "conflict"; message: string }
    | { ok: false; kind: "error"; message: string };

/** A short weekly note staff write to the merchant (#3673 / backend T10). */
export interface ClientNote { id: string; date: string; body: string; author: string }

export type ApprovalRequirement = "required" | "not_required";
export type ApprovalSource = "auto" | "staff";
export interface ApprovalOverride {
    taskId: string;
    requirement: ApprovalRequirement;
    source: ApprovalSource;
    /**
     * Whether a task the merchant is NOT asked to approve still appears in their
     * "in hand, needs nothing from you" list. Defaults to true, both here and in
     * the server's schema — which is why it must be sent on every write: the
     * server assigns it unconditionally, so a PUT carrying only `requirement`
     * silently switches it back on.
     */
    showToClient: boolean;
    /**
     * H20a, additive: a staff override that waives the client's yes records the
     * named person, when, why, and where the client said yes. Absent on an
     * override set before H20a, and on the automatic ones.
     */
    setBy?: string;
    setAt?: string;
    reason?: string;
    whereClientSaidYes?: string;
}

/** H20a: who records an override, why, and where the client said yes. */
export interface ApprovalOverrideReason {
    name: string;
    reason: string;
    whereClientSaidYes: string;
}

/** What the merchant is asked to do. Every kind also takes a thread. */
export type ReviewItemKind = "approve" | "choose" | "provide_file";

/**
 * The merchant's answer. Separate from the thread: approval answers "may we do
 * this?", the thread is the conversation about it.
 */
export type ClientDecision = "pending" | "approved" | "changes_requested" | "deferred" | "answered";

export type Audience = "client" | "staff";

export interface PackItem {
    id: string;
    packId: string;
    taskId: string;
    /** See `noDecisionNeeded` — the same two fields, on the item a merchant is
     *  asked to decide (ourteam-backend#4918). */
    templateKey?: string | null;
    evidence?: Record<string, unknown> | null;
    kind: ReviewItemKind;
    title: string;
    why: string;
    tags: string[];
    current?: string;
    proposed?: string;
    outline?: string[];
    options?: string[];
    /** Bumped every time staff change `proposed` after publish. */
    version: number;
    decision: ClientDecision;
    decidedAt: string | null;
    decidedBy: string | null;
    answer: string | null;
    fileName: string | null;
    /** The presigned link beside `fileName` (T14). Absent on a payload from before it. */
    fileUrl?: string | null;
    /** Who the item is waiting on right now. Null once decided. */
    waitingOn: Audience | null;
    // The four fields below are OPTIONAL and absent on live today. Absent means
    // unknown, never "not late" or "holds nothing up": a reader without them
    // says when the ask was sent and makes no claim about a deadline.
    /**
     * The date the answer is wanted by, date-only (`2026-09-22`).
     * BE handoff: "Reply-by date and 'holds up' on merchant asks".
     */
    replyBy?: string | null;
    /**
     * Whole calendar days past `replyBy`, 0 when not late. COMPUTED BY THE SERVER and
     * read as given: working it out here from today's date is the mistake
     * `aiCheck.nextAt` made. Same handoff.
     */
    lateDays?: number | null;
    /** The work this answer holds up, in the merchant's words ("9 pages"). Same handoff. */
    holdsUp?: string | null;
    /** The hint inside a free-text answer box. BE handoff: "Answer hint (placeholder) per ask". */
    placeholder?: string | null;
}

export type PackBlock =
    | { id: string; type: "section"; heading: string; body: string }
    | { id: string; type: "table"; title: string; columns: string[]; rows: string[][] }
    /**
     * A screenshot. `fileKey` is what the composer writes; the read side adds
     * `fileUrl` (presigned) and `fileName`, so the merchant's page shows the
     * image rather than a striped placeholder.
     */
    | { id: string; type: "evidence"; caption: string; label: string; fileKey?: string | null; fileUrl?: string | null; fileName?: string | null }
    | { id: string; type: "decision"; itemId: string };

export type PackStatus = "draft" | "published" | "submitted";

export interface Pack {
    id: string;
    businessId: string;
    ref: string;
    title: string;
    intro: string;
    status: PackStatus;
    blocks: PackBlock[];
    items: PackItem[];
    preparedBy: string;
    createdAt: string;
    publishedAt: string | null;
    submittedAt: string | null;
}

export type MessageKind = "message" | "version" | "decision" | "call_request" | "suggested_edit";

export interface ThreadMessage {
    id: string;
    kind: MessageKind;
    authorKind: Audience | "oura";
    authorName: string;
    body: string;
    fileName: string | null;
    /** For kind "suggested_edit": the merchant's rewrite of `proposed`. */
    suggestedEdit: { before: string; after: string; accepted: boolean } | null;
    at: string;
}

export interface Thread {
    id: string;
    itemId: string;
    businessId: string;
    status: "open" | "resolved";
    messages: ThreadMessage[];
}

export interface Notification {
    id: string;
    audience: Audience;
    businessId: string;
    title: string;
    body: string;
    itemId: string | null;
    packId: string | null;
    read: boolean;
    at: string;
    /**
     * Where the bell entry opens, a host-relative path such as
     * `/oura/decisions?doc={id}` or `/oura/work?request={id}`. Optional and
     * absent on live today: BE handoff "Notification destination link".
     */
    link?: string | null;
}

/**
 * The in-app path a bell entry opens, or null when it names none. Only a
 * path inside Oura is followed ("/oura/..."), so a stray absolute or
 * protocol-relative link never leaves the app.
 */
export function noticeLink(n: Pick<Notification, "link">): string | null {
    const link = n.link?.trim();
    return link && link.startsWith("/oura/") && !link.startsWith("//") ? link : null;
}

export interface CallRequest {
    id: string;
    businessId: string;
    itemId: string | null;
    requestedBy: string;
    preference: string;
    note: string;
    status: "open" | "scheduled" | "closed";
    at: string;
}

/**
 * A change the merchant asked for from Work ("Ask for a change"), the one way
 * a merchant starts a request. The monthly small change (G5) starts here.
 *
 * The docs phase 1 contract (map_contract.md A.3, routes 13 to 17, BE handoff
 * B9): POST /businesses/mine/change-requests {note, requestedBy, fileKeys},
 * GET /businesses/{id}/change-requests, POST /change-requests/{id}/reply and
 * PATCH /change-requests/{id}. Status is open, answered or done; a reply sets
 * answered unless it is done. Not a call request: that route's notice says
 * "asked for a call", which would mislead the account manager.
 */
/** A screenshot sent with a staff reply, with a link that opens it (presigned on every read). */
export interface ReplyFile {
    fileKey?: string;
    name: string;
    url: string;
}

export interface ChangeRequest {
    id: string;
    businessId: string;
    requestedBy: string;
    /** Where Angel's reply goes. Null when the server does not know it. */
    requesterEmail: string | null;
    note: string;
    files: Array<{ fileKey: string; name: string; url: string }>;
    status: RequestStatus;
    /** Angel's reply, sent from oura@. `files`: screenshots sent with it (absent on an older server). */
    reply: { body: string; by: string; at: string; files?: ReplyFile[] } | null;
    /** Every reply, oldest first; `reply` is the last (#6874). Absent on an older server: see everyReply. */
    replies?: Array<{ body: string; by: string; at: string; files?: ReplyFile[] }>;
    /** Filed as the month's small change (G5). `month` is "2026-10". */
    smallChange: { month: string; termMonth: number | null; filedBy: string; filedAt: string } | null;
    at: string;
    doneAt: string | null;
    doneBy: string | null;
    /**
     * What Oura read out of the client's message (#6276, same row as the staff
     * Requests page). Absent on an older server and on requests asked in Oura
     * before the reader: then the note alone stands for the ask.
     */
    title?: string | null;
    where?: string | null;
    why?: string | null;
    checklist?: string[];
    /** The key message ("Key message: ...") first, then what to get right. */
    notes?: string[];
    source?: "oura" | "text" | "screenshot" | "email";
    /** What the client actually sent, kept whole. */
    original?: { text: string; fileKeys: string[]; email: { from: string; subject: string; messageId: string } | null } | null;
}

/**
 * A change request as the merchant reads it on Work ("Your requests"): route
 * 18, GET /businesses/mine/change-requests (BE handoff B9). The same row as
 * ChangeRequest without what only staff need: no business id, no requester
 * email, no file keys, and of the small change only its calendar month.
 */
export interface MyChangeRequest {
    id: string;
    /** Who at the business sent it, as they signed it. */
    requestedBy: string;
    note: string;
    /** Each file with a link that opens it (presigned on every read). */
    files: Array<{ name: string; url: string }>;
    status: RequestStatus;
    /** Our newest reply, sent from oura@, with any screenshots sent with it. */
    reply: { body: string; by: string; at: string; files: ReplyFile[] } | null;
    /** Every reply, oldest first; `reply` is the last (#6874). Empty on an older server: see everyReply. */
    replies: Array<{ body: string; by: string; at: string; files: ReplyFile[] }>;
    /**
     * Filed as a calendar month's small change (G5): "2026-10", Malaysian
     * time. `filedAt` (additive, B9) orders 2 filed in one month, so the
     * later one reads as an extra; absent on an older server.
     */
    smallChange: { month: string; filedAt?: string | null } | null;
    at: string;
    doneAt: string | null;
}

/**
 * A call the merchant asked for, as they read it on Work ("Your requests"):
 * route 19, GET /businesses/mine/call-requests (BE handoff B9). Everything
 * the client started shows there, calls as well as changes (YW, 29 Sep
 * 2026). `about` is the title of the question the call is on, joined by the
 * server; null for a call on no question, or one whose question is gone.
 */
export interface MyCallRequest {
    id: string;
    requestedBy: string;
    about: string | null;
    preference: string;
    note: string;
    status: CallRequest["status"];
    at: string;
}

export interface MyBusiness {
    id: string;
    name: string;
    city: string;
    /**
     * PROPOSED (BE ticket "Business favicon, read with the scan"): the site's
     * own icon, found when the business is scanned. Absent or null today, and
     * the rail draws a building, as it always has.
     */
    faviconUrl?: string | null;
}

/**
 * THE PLAN. Seeded once from the quotation (clients/<slug>/engagement.json, written by
 * the quotation build script), then owned by the internal dashboard: staff move lines
 * between months, change quantities, add lines and set the start date here. The
 * merchant's Progress reads this same record, so the two always correspond, and both
 * correspond to the quotation because they started from it. Every edit is logged.
 *
 *   GET   /businesses/{id}/engagement                staff+merchant → Engagement
 *   PUT   /businesses/{id}/engagement                staff → Engagement   (seed: the JSON verbatim)
 *   PATCH /businesses/{id}/engagement                staff → Engagement   (edits, appends to changeLog)
 */
export interface Deliverable {
    code: string;
    label: string;
    months: number[];
    quantity: string | null;
    cadence: string | null;
    note: string | null;
    /**
     * The audit check keys this line is responsible for moving
     * (ourteam-backend#4814). Authored by the server from its ONE catalogue map
     * (`aeo_catalogue.COVERS`) at seed time — the frontend never types a mapping
     * and must not offer to edit one.
     *
     * `[]` is an answer, not a gap: the line covers no measured check.
     */
    covers: string[];
    /**
     * True when `covers` is empty. "Reported by hand" and "not started" are
     * DIFFERENT FACTS: a term review or a call is accounted for manually and
     * that is legitimate, while a line with checks and nothing measured is work
     * that has not landed.
     *
     * SNAKE CASE, and deliberately so. The engagement's `deliverables` is a
     * JSONB column the server returns verbatim
     * (`OuraEngagement.to_dict` → `list(self.deliverables or [])`), and
     * `_validate_deliverables` writes the row as `reported_by_hand`. The client
     * page's `ClientPlanItem` is built fresh per request and camel-cases the
     * same fact as `reportedByHand` — two shapes, on purpose, because they come
     * from two different places. Typing this camelCase would read `undefined`
     * off every row and drop the field on the next save, which is the exact
     * failure this ticket exists to prevent. The write side accepts either
     * spelling (`populate_by_name`), so the round trip sends back what it read.
     */
    reported_by_hand: boolean;
}
export interface EngagementChange { at: string; by: string; summary: string }
/** One `engagement.documents[]` entry. See `Engagement.documents`. */
export interface EngagementDocument {
    title?: string;
    file?: string;
    ref?: string;
    /** e.g. `"quotation"`, `"execution_timeline"`. */
    kind?: string;
    /** ISO date. */
    issued?: string;
    version?: string;
    summary?: string;
}

/** `engagement.tracking`: the keys a screen reads, and everything else kept as it came. */
export interface EngagementTracking {
    /** 5 to 10 phrases, trimmed, no duplicates. Absent or [] = not recorded yet. */
    priority_keywords?: string[];
    /**
     * The two signature questions staff recorded on the contract. A part left
     * out is picked from the AI check by the server; a part sent blank is
     * removed, so the check's pick applies again (ourteam-backend#5256).
     */
    signature?: { visibility?: string; coverage?: string };
    [key: string]: unknown;
}

/** One outlet as the plan stores it. The slug is the server's and never typed by hand. */
export interface OutletName {
    slug: string;
    name: string;
    /**
     * The outlet's address and phone as the signed quote gives them
     * (ourteam-backend#5808). Absent on a backend that does not send them yet
     * and null on an outlet nobody has entered them for: neither is "".
     */
    address?: string | null;
    phone?: string | null;
}

/** One outlet as the engagement PATCH takes it when its quote details change (ourteam-backend#5808). */
export interface OutletNameSent {
    name: string;
    address?: string | null;
    phone?: string | null;
}

/**
 * Where a field of the business record stands (ourteam-backend#5808).
 *
 *   match     both sides are recorded and agree after the backend's own
 *             normalising (trim, collapse spaces, case, phone digits with +60 as 0)
 *   mismatch  both sides are recorded and differ
 *   missing   a side is not recorded yet; never a mismatch
 */
export type RecordCheckState = "match" | "mismatch" | "missing";

/** One field, as the signed quote and the Google profile each hold it. A side that is not recorded is null. */
export interface RecordCheckField {
    quote: string | null;
    google: string | null;
    state: RecordCheckState;
}

/**
 * Whether the business record agrees with the signed quote and Google, read-only
 * (ourteam-backend#5808). On the engagement payload; null for a business with no
 * engagement, and absent on a backend that does not send it yet: a screen draws
 * nothing for either rather than a row of "missing".
 */
export interface BusinessRecordCheck {
    business: { name: RecordCheckField; website: RecordCheckField };
    outlets: Array<{
        /** "" for a business with one outlet. */
        slug: string;
        /** The outlet's own name, as the check was built (ourteam-backend#5877); absent on an older backend. */
        name?: string;
        confirmed?: boolean;
        address: RecordCheckField;
        phone: RecordCheckField;
    }>;
    /** The backend's own tally, `total` being the fields compared. The card counts the rows itself and does not read it. */
    counts?: { match: number; mismatch: number; missing: number; total: number };
}

export interface Engagement {
    ref: string;
    client: string;
    planName: string;
    termMonths: number;
    pageUnits: number;
    outlets: number;
    /**
     * The outlets' names, as the server stores them: `{slug, name}` per outlet.
     * Null until staff confirm them.
     *
     * READ and WRITE are different shapes on purpose. The PATCH takes plain
     * strings in contract order and the server keeps the slug that sits at each
     * position, so a RENAME changes the name and never the slug — which is what
     * keeps a tick attached to its outlet (`name_outlets`, BE #5156).
     */
    outletNames?: OutletName[] | null;
    /** The business record against the signed quote and Google (ourteam-backend#5808). Staff read it; see `BusinessRecordCheck`. */
    businessRecordCheck?: BusinessRecordCheck | null;
    /**
     * Names the newest usable scan found (its branch list), for staff naming
     * the outlets. [] when no scan offers any, and there may be FEWER of them
     * than the contract sells: they are suggestions, not the answer. Staff
     * only; absent from the merchant's copy.
     */
    outletSuggestions?: string[];
    /**
     * The outlets the client named in the Setup form ("Where are your
     * outlets?"), once the form is sent; [] before (ourteam-backend#6407).
     * Staff only.
     */
    outletAreasFiled?: string[];
    /** When the names were confirmed, and by whom. Null while they are not. */
    outletNamesConfirmedAt?: string | null;
    outletNamesConfirmedBy?: string | null;
    startedAt: string | null;
    /**
     * Whether the plan's dates skip Malaysian public holidays as well as
     * weekends (Jack, 22 Oct 2026). Absent until the backend serves it, and
     * absent means unknown: the switch is not shown and today's rule (skip
     * them) stands. The Execution Timeline build reads the same setting.
     */
    skipPublicHolidays?: boolean | null;
    deliverables: Deliverable[];
    deviations: string[];
    /**
     * Free-form JSONB, and it carries two shapes today. A seeded engagement
     * writes `{title, file}`; an activated one writes `{ref, kind, issued,
     * version?}` with no title and no file at all — The Lush Clinic's two
     * documents are `{kind: "quotation", ref, issued}` and
     * `{kind: "execution_timeline", issued, version}`. Every field is optional
     * so a reader has to handle both (the backend's own `_reports` already does:
     * `title or file or "Document"`).
     */
    documents: EngagementDocument[];
    changeLog: EngagementChange[];
    /**
     * The contract's tracking facts, free-form JSONB on the server and read
     * back as sent. `priority_keywords` is the only one a screen edits today
     * (5 to 10, captured at the quotation discussion): the schedule cannot be
     * published while it is short, and the merchant then never sees a date
     * (ourteam-backend#5256).
     *
     * Null until a quotation seeds one. Absent is unknown, not empty, so a
     * screen says "not recorded" rather than "0 keywords". Keys other than
     * the ones named here are kept and sent back untouched: the baseline and
     * `source_assessment_id` live in here too, and an edit must not drop them.
     */
    tracking?: EngagementTracking | null;
    /** When the contract last changed, as the read returns it; sent back as `expectedUpdatedAt` so two edits never overwrite each other (H27). */
    updatedAt?: string | null;
}

/**
 * ACTIVATION — `POST /businesses/{id}/activate` (ourteam-backend#4809, P3-1).
 *
 * Seeds the plan, binds the workspace and mints the merchant login in ONE
 * server-side transaction. Decision D3, recorded on #4809: there is no DELETE
 * route for an engagement, so a client-orchestrated sequence could not roll back
 * its first step, and a failure after the seed would leave a business carrying a
 * plan it was never activated for. The screen therefore never issues the seed,
 * the binding and the login as separate requests.
 */
/**
 * A deliverable as a SEED sends it, which is not the same shape as a stored one.
 *
 * `covers` and `reported_by_hand` are optional here on purpose: the server
 * authors `covers` from its own catalogue (`aeo_catalogue.COVERS`) when the
 * caller sends none, and marks the line reported-by-hand when that comes back
 * empty. Absent means "author it for me"; `[]` means "this line covers nothing,
 * and I mean it". Sending `[]` for every line of an imported quotation would
 * therefore switch catalogue authoring OFF for the whole plan — which is why
 * this type exists rather than reusing the stored `Deliverable`, where both
 * fields are always present.
 */
export type DeliverableSeed = Omit<Deliverable, "covers" | "reported_by_hand"> & {
    covers?: string[];
    reported_by_hand?: boolean;
};

/** An engagement as it is sent to be seeded. See `DeliverableSeed`. */
export type EngagementSeed = Omit<Engagement, "changeLog" | "deliverables"> & {
    deliverables: DeliverableSeed[];
};

export interface ActivationInput {
    engagement: EngagementSeed;
    contact: { email: string; name: string };
}

/** 201 body. Everything the screen needs, so it does not have to ask again. */
export interface ActivationResult {
    business: { id: string; domain: string; businessName: string; companyId: string | null };
    engagement: Engagement;
    merchant: { email: string; userId: string; subdomain: string; workspaceUrl: string };
}

/**
 * A refusal the screen has to render, not a crash.
 *
 * `code` is the server's own (`already_activated`, `email_in_another_workspace`,
 * `invalid_engagement`, `activation_failed`, `merchant_login_missing`) and
 * `message` is the sentence it wrote. Both are shown verbatim: the server knows
 * why it refused and has already phrased it for a person.
 */
export interface ActivationFailure {
    code: string | null;
    message: string;
}

export type ClientPlanStatus =
    | "done"
    | "in_progress"
    | "waiting_on_you"
    | "with_google"
    | "not_started"
    /** Covers no measured check, so it is accounted for manually. NOT "not started". */
    | "reported_by_hand";

/**
 * The current reading of one check a plan line covers (ourteam-backend#4814).
 *
 * Computed on read from the newest usable assessment and never stored beside the
 * contract, so it is always today's number rather than one that was true once.
 */
export interface MeasuredCheck {
    /** The check's own label, e.g. "Broken pages (4xx)". */
    label: string;
    severity: string | null;
    /** Failing pages. `0` means measured and nothing is wrong. */
    count: number;
    state: "clear" | "failing";
}

export interface ClientPlanItem {
    code: string;
    /**
     * The Delivery deliverable's key ("S2-m1-holland-village", "G2-m1"):
     * unique where a code repeats (an outlet, an earlier month still open).
     * Absent = the code is unique.
     */
    key?: string | null;
    label: string;
    status: ClientPlanStatus;
    note: string | null;
    noteTone: "ok" | "warn" | "muted";
    /** Check keys this line moves. Mirrors `Deliverable.covers`. */
    covers: string[];
    /** Camel case here, snake case on `Deliverable` — see the note there. */
    reportedByHand: boolean;
    /**
     * The pending pack items this line waits on, so a "Needs you" row opens the
     * exact ask. Optional, absent on live today: without it the row opens its
     * own work drawer, never the answers. BE handoff: "Plan line links and
     * descriptions".
     */
    itemIds?: string[];
    /** What the line is, in the merchant's words. Same handoff; absent = build it from the deliverable. */
    description?: string | null;
    /**
     * Keyed by check key, one entry per `covers` key.
     *
     * `null` IS A REAL ANSWER: the check was never measured for this business,
     * no assessment carries it. That is not the same fact as `{ count: 0 }`,
     * which is "we looked and nothing is wrong". The server is careful never to
     * send a zero for the first, and the screen must be equally careful.
     */
    measured: Record<string, MeasuredCheck | null>;
    /**
     * Where the line stands, in the client's words (Delivery's client line,
     * H6/H17): "In progress. 1 of 4 done on your 10 key pages." Optional.
     * fixture-only until ourteam-backend handoff: Delivery client line
     */
    line?: string | null;
    /** What we fixed on this line, one short phrase each ("Summary lines on 6 pages"). Optional. */
    fixed?: string[];
    /** The plan month the line belongs to, when it is earlier than this month and still open. */
    month?: number | null;
}

/**
 * THE TIMELINE — `GET /businesses/{id}/timeline` (ourteam-backend#4821, T-6).
 *
 * Staff only. The merchant's view of what changed is T-7 and is a DIFFERENT
 * payload: this one carries conflicts and a publisher's name, neither of which
 * a merchant may see.
 *
 * SNAKE_CASE INSIDE `Schedule`, and deliberately not "tidied" on the way in.
 * The schedule is the date engine's own dict, stored verbatim in the published
 * snapshot and compared field-by-field by `diff_schedules` — renaming its keys
 * on this side would mean the diff names fields the screen cannot find. The
 * wrapper around it is camelCase because the router builds that fresh.
 */
export interface ScheduleMonth {
    term_month: number;
    /** "1 to 28 Sep 2026" — already formatted by the engine. */
    window: string;
    start: string;
    end: string;
    pages_this_month: number;
    pages_cumulative: number;
    report_date: string;
    report_date_text: string;
    /** What lands this month, in the merchant's words. */
    lands: string[];
}

export interface Schedule {
    client: string;
    reference: string;
    plan_label: string;
    term_months: number;
    /** "contract" or "catalogue" — which one the engine had to fall back on. */
    term_months_source: string;
    total_units: number;
    total_units_source: string;
    trigger_date: string;
    trigger_date_text: string;
    clock_start: string;
    clock_start_text: string;
    handover: string;
    handover_text: string;
    term_end: string;
    term_end_text: string;
    keywords_by: string;
    keywords_by_text: string;
    priority_keywords: string[];
    holidays: string[];
    months: ScheduleMonth[];
    version: string;
    prepared_by: string;
    issued: string;
    conflicts: TimelineConflict[];
}

/**
 * The contract and the catalogue disagreeing about something signed.
 *
 * `code` is stable and a screen may switch on it; `message` is the sentence a
 * reviewer reads and may be reworded. Both exist because the engine used to
 * write these to stderr, where a screen could not switch on them and a person
 * never saw them — which is how a schedule that contradicted a signed contract
 * reached a merchant.
 */
export interface TimelineConflict { code: string; message: string }

/** One date that moved. Render `label`, never the raw `field`. */
export interface DateMove {
    scope: "term" | "month";
    /** Null for the term-level dates (clock start, handover, term end). */
    termMonth: number | null;
    field: string;
    /** "Work starts", "Report", "Term ends" — the words to put on screen. */
    label: string;
    /** Null on a FIRST publish: the whole schedule is new, not changed. */
    from: string | null;
    to: string | null;
}

export interface PublishedTimeline {
    id: string;
    version: number;
    datesMoved: number;
    diff: DateMove[];
    publishedBy: string;
    publishedAt: string;
    schedule?: Schedule;
}

export interface TimelinePayload {
    business: { id: string; domain: string };
    /** An edit has landed and has not been published. The merchant has not been told. */
    changePending: boolean;
    /** Null before the first publish — NOT an empty snapshot. "Never published"
     *  and "published, and nothing has moved since" are different answers. */
    published: PublishedTimeline | null;
    /** Null when a schedule cannot be computed at all; `problems` says why. */
    computed: Schedule | null;
    problems: string[];
    conflicts: TimelineConflict[];
    /** What WOULD move if you published now. */
    diff: DateMove[];
    datesMoved: number;
    canPublish: boolean;
}

/** What kind of work an activity row was, for its icon. */
export type ClientWorkKind = "site" | "page" | "ai" | "google" | "report" | "sent" | "call";

export interface ClientActivityEntry {
    id: string;
    at: string;
    body: string;
    meta: string | null;
    /**
     * What the entry points at. `url` is optional and absent on live today
     * (handoff below). When sent it is either an attachment at the signed-in
     * API path `/api/v1/activity/attachments/{id}` (ourteam-backend#4573),
     * read with `fetchActivityAttachment`, or the merchant's own live page.
     */
    evidence?: Array<{ label: string; url?: string | null }>;
    // Optional, absent on live today. BE handoff: "Activity kind, why it
    // matters, evidence links". Absent = one neutral icon and no "why".
    kind?: ClientWorkKind | null;
    /** Why the work matters, in merchant-safe words (the task's own why). */
    why?: string | null;
}
export interface ClientDecisionRow { id: string; title: string; decision: ClientDecision; decidedAt: string | null; workStatus: string }
/** A report the merchant can open in the viewer or download. `url` is a PDF; the BE presigns it. */
export interface ClientReportLink {
    /** A scan report's V number (ourteam-backend#5900), the one in its title. Absent on other kinds and older backends. */
    scanNumber?: number | null;
    /**
     * OPAQUE, and sent back exactly as received (ourteam-backend#5414 C-BE-1).
     * An uploaded report's id is a uuid, an assessment's is `assessment-<id>`
     * and an engagement document's is `doc-<16 hex>` -- it was `doc-0` until
     * that review, so nothing may parse or rebuild it.
     */
    id: string;
    title: string;
    date: string;
    url: string | null;
    summary?: string;
    /** Null when the server could not count them (an assessment's rendered report). */
    pages?: number | null;
    /**
     * WHY `url` is null, in the `*Absent` shape the rest of this payload uses.
     * `"not_uploaded"` is an engagement document that lives only on our side:
     * it says nothing about whether it was emailed. `"in_app"` is the Baseline
     * Snapshot, which has no file: it is drawn here from its scan (#4457).
     */
    urlAbsent?: string | null;
    /** The Baseline Snapshot's scan, read from GET /assessments/{id}/baseline-snapshot (ourteam-backend#6211). */
    assessmentId?: string | null;
    // Optional, absent on live today. BE handoff: "Report kind and sections".
    /** "doc_approved" (B24): the PDF of a document version the client approved, filed by a named person. */
    kind?: "assessment" | "snapshot" | "upload" | "document" | "doc_approved" | "baseline_snapshot";
    /** The headings inside, in the merchant's words. Null = not recorded. */
    sections?: string[] | null;
    /** The term month this report closes. BE handoff: "Report tied to its term month". */
    termMonth?: number | null;
    /**
     * When the CLIENT first opened it, or null while they have not (C-BE-1).
     * Staff reading a report never writes it, so this stays the client's own
     * fact and the staff list can say "Opened 8 Oct" from the same field.
     */
    readAt?: string | null;
    /**
     * Whether the client has still not opened it. Absent on a payload stored
     * before C-BE-1, which reads as "not known", never as unread -- a "New"
     * tag on every report a client had already read would be worse than none.
     *
     * A report with no file is never unread: there is nothing to open.
     */
    unread?: boolean;
}

/** One search that brought visits. BE handoff: "Top searches by clicks on the client page". */
export interface ClientTopQuery { query: string; clicks: number; branded: boolean }

/**
 * The server's reason codes for a number it did not send (#4808, #5076,
 * #5083). A reader shows the honest absent state for each and never a zero.
 *
 * A bare null could not tell two different situations apart, and the screen
 * printed the same "Not measured yet" over both. They are not interchangeable:
 * `no_assessment` means nothing has been scanned, while `not_judged` means the
 * scan ran but produced only the lexical score, which is deliberately never
 * shown to a merchant. One says "wait for the first scan", the other says
 * "wait for the next one".
 */
export type CoverageAbsent = "no_assessment" | "not_judged";
export type AppearingForAbsent = "not_connected" | "no_previous_window";
export type AiCheckAbsent = "no_assessment" | "no_app_measurement";

/** One Coverage Set reading: how many of the 50 questions the site itself answers. */
export interface CoverageReading { answered: number; total: number; score?: number | null; measuredAt: string | null }

export interface ClientStats {
    /**
     * `previous` is the reading before this one, as a READING, not a number:
     * the wire has always sent `{answered, total, score, measuredAt}` here, and
     * typing it as a number turned "answered minus previous" into NaN.
     */
    coverage: (CoverageReading & { previous: CoverageReading | null }) | null;
    /** Why `coverage` is null. Sent by the server beside it; absent on an older payload. */
    coverageAbsent?: CoverageAbsent | null;
    pages: { published: number; planned: number };
    search: {
        windowDays: number;
        clicks: number;
        clicksPrevious: number | null;
        impressions: number;
        impressionsPrevious: number | null;
        measuredAt: string;
        /** Top searches by clicks. Optional, absent on live today; see `ClientTopQuery`. */
        topQueries?: ClientTopQuery[];
    } | null;
    /** Empty, not null, when there is nothing to show: `appearingForAbsent` says why. */
    appearingFor: string[];
    appearingForAbsent?: AppearingForAbsent | null;
    /**
     * `nextAt` is NULL without a start date (#4808). It used to be computed from
     * today when there was none, so the promised date slid a day later every day.
     * It was once typed `string`, so both places that print it fed it straight
     * to `shortDate`: twelve of the sixteen businesses on staging rendered
     * "Your first AI check runs on Invalid Date". Never format it unguarded.
     */
    aiCheck: { band: string | null; measuredAt: string | null; bandAbsent?: AiCheckAbsent | null; nextAt: string | null };
    /**
     * `"claimed"` is what the server sends for a connected profile; the other
     * values predate it. No screen may render this as a row (#3801): the
     * connector is not in the catalogue, so the status is read, never shown.
     */
    googleProfile: { status: "claimed" | "verified" | "verifying" | "not_claimed"; note: string };
}

/**
 * A dated milestone the merchant is waiting for. BE handoff: "Upcoming dated
 * milestones for the merchant". Future only, date ascending, date-only.
 */
export interface ClientUpcomingDate {
    date: string;
    label: string;
    kind: "handover" | "golive" | "report" | "ai_check" | "call" | "other";
    /** The date it was, when it moved. Null when it never moved. */
    was: string | null;
    /** Why it moved, in the merchant's words. */
    reason: string | null;
    /** The ask it waits on, when it waits on one. */
    itemId: string | null;
}

/** Which state the business is in, derived by the server (#4807). Read this rather than inferring one from a zero. */
export interface ClientLifecycle {
    state: "prospect" | "quoted" | "signed" | "active" | "ended";
    /** Null when the server has no date for the transition (an ended business, today). */
    since: string | null;
}

/** The pages behind the "Pages published" count. BE handoff: "Published and upcoming page list". */
export interface ClientPageList {
    /** `kind`: a new page, or an existing one improved (Delivery's page slots, H16/H29). Optional. */
    published: Array<{ title: string; url: string | null; publishedAt: string; kind?: "new" | "improved" }>;
    upcoming: Array<{ title: string; dueAt: string | null }>;
}

/**
 * THE MERCHANT'S OWN SCHEDULE (ourteam-backend#4916, T-7).
 *
 * A DIFFERENT PAYLOAD from the staff review one. That endpoint carries
 * `conflicts[]` and the publisher's name; this carries neither, and nothing on
 * a merchant surface may read the staff route to get them.
 *
 * Every date here is DATE-ONLY, because `shortDate` parses a date-only string
 * and appends `T00:00:00`. A full ISO timestamp renders as "Invalid Date", and
 * a UTC midnight formatted locally renders the day before east of Greenwich.
 * `publishedAt` is the one timestamp, and is labelled as such.
 */
export interface MerchantScheduleMonth {
    termMonth: number;
    /** "18 Sep to 17 Oct 2026" — already written by the engine. */
    window: string;
    reportDate: string;
    /** What lands this month, in the merchant's words. */
    lands: string[];
    /**
     * New pages due this month. The engine stores it (`pages_this_month` on the
     * staff schedule) but the merchant payload drops it. Optional, absent on
     * live today. BE handoff: "Upcoming dated milestones for the merchant".
     */
    pagesThisMonth?: number | null;
}

/** One date that moved, in words a merchant can act on. */
export interface MerchantDateMove {
    /** "Work starts", "Report" — never the raw field name. */
    label: string;
    /** Null on the first publish: the schedule is new, not changed. */
    from: string | null;
    to: string | null;
}

export interface MerchantSchedule {
    version: number;
    /** The one timestamp on this block. */
    publishedAt: string;
    clockStart: string | null;
    termEnd: string | null;
    /**
     * Which month of the term it is, COMPUTED BY THE SERVER with the anniversary
     * rule (`plan_month` / `aeo_timeline.term_month`).
     *
     * Read, never recomputed. `monthOf()` exists on this side and agrees, but
     * calling it here would compute a date in the browser from today — the rule
     * P2-4 established with `aiCheck.nextAt`, which slid forward by a day every
     * day. The same server number also feeds Progress, which is what makes the
     * two surfaces agree rather than merely intend to.
     */
    currentMonth: number;
    months: MerchantScheduleMonth[];
    /** Null unless the latest publish actually moved dates. */
    changed: { datesMoved: number; summary: string; moves: MerchantDateMove[] } | null;
}

/** Everything the merchant's screens render, scoped by their login. */
export interface ClientPagePayload {
    business: MyBusiness;
    viewer: { name: string };
    /**
     * The account manager, or NULL when nobody is assigned (#4808). The server
     * used to answer "ourteam" for nobody, a preparer that does not exist; the
     * screen now writes its own fallback.
     */
    preparedBy: string | null;
    /**
     * Where the merchant writes to reach their account manager (the shared
     * Oura inbox). Optional and absent on live today: without it no merchant
     * screen shows or links an email. BE handoff: "Client email reply-to"
     * (the account-manager contact on the client page payload).
     */
    contactEmail?: string | null;
    updatedAt: string;
    /** True when a staffer read this through `getClientPageFor` (#4768). Never set on the merchant's own read. */
    previewedByStaff?: boolean;
    /** Sent by the server since #4807; optional so an older payload still types. */
    lifecycle?: ClientLifecycle;
    stats: ClientStats;
    packs: Pack[];
    threads: Thread[];
    /**
     * Where the engagement is. Month 1 is the Foundation build (12 deliverables);
     * every month after is Growth (the 8 lines from the catalogue). Progress is
     * the share of this phase's items done. No day counting, no live date.
     */
    plan: { phase: "foundation" | "growth"; month: number; items: ClientPlanItem[] } | null;
    /**
     * The live plan record the two sides share. `plan` and `subscription` are
     * derived from it.
     *
     * NULLABLE, because a business can exist before anyone has been sold
     * anything (#3710 AC3). It used to be non-null and the backend kept the
     * promise with a sentinel — an engagement of zeros — which the merchant
     * surfaces then rendered as real: "0 of 0 pieces of work" over "month 1 of
     * 0". ourteam-backend#4807 removes the sentinel; this is the type being
     * ready for the null it will send, and the readers below handling it.
     */
    engagement: Engagement | null;
    /**
     * What they bought, from the quotation. The source is `clients/<slug>/engagement.json`,
     * written by the quotation build script beside the PDF (see
     * $OURA_ROOT/clients/_quotations/engagement.py). `startedAt` is null until payment clears.
     */
    subscription: {
        ref: string;
        name: string;
        startedAt: string | null;
        termMonths: number;
        pagesPublished: number;
        pageUnits: number;
        outlets: number;
        /** H29: every page on the quote (month 1's in Foundation), done when its slot is done, however it went live. Absent until the server sends it. */
        pagesOnQuote?: { done: number; total: number } | null;
    } | null;
    /**
     * The newest weekly note. `author` is optional and absent on live today:
     * the stored note has one, the payload drops it. Without it the screen
     * names `preparedBy`, who may not have written it. BE handoff: "Friday
     * note author on the merchant payload".
     */
    weekNote: { date: string; body: string; author?: string } | null;
    /** Optional, absent on live today; see `ClientUpcomingDate`. Absent = build "coming up" from the schedule. */
    upcoming?: ClientUpcomingDate[];
    /** Optional, absent on live today; see `ClientPageList`. Absent = the counts in `stats.pages` only. */
    pages?: ClientPageList;
    activity: ClientActivityEntry[];
    decisions: ClientDecisionRow[];
    reports: ClientReportLink[];
    /**
     * How many reports the client has not opened (C-BE-1). Always present once
     * that shipped; absent reads as "not known" and draws no badge.
     */
    unreadReports?: number;
    /**
     * How many shared documents the client has not opened (ourteam-backend#6108).
     * Absent reads as "not known" and draws nothing.
     */
    unreadDocuments?: number;
    noDecisionNeeded: Array<{
        label: string;
        status: "done" | "in_progress" | "not_started";
        /**
         * `"contradiction"` when this is one (ourteam-backend#4918). The screen
         * switches on THIS rather than on the shape of `evidence`, because
         * `_activity` writes a different `evidence` under the same key —
         * attachment labels — and sniffing would render those as a contradiction.
         */
        templateKey?: string | null;
        /**
         * The merchant-safe evidence block, or null for every other kind of
         * task. Already stripped of `assessmentId` server-side: an internal id
         * is nothing a merchant can act on, the same rule `deviations` and the
         * publisher's name follow.
         */
        evidence?: Record<string, unknown> | null;
    }>;
    /**
     * NULL, not an empty object, when nothing has ever been published — "no
     * schedule yet" and "a schedule with no months" are different facts, and
     * #4807 already removed one sentinel of exactly this kind from this payload.
     *
     * Optional on the way in as well: a staging or production payload from
     * before #4916 carries no key at all, and a merchant on that build must see
     * the named empty state rather than a crash.
     */
    schedule?: MerchantSchedule | null;
    /**
     * The Google Docs shared with the client (docs phase 1, route 12). Absent
     * = Oura doesn't read docs for this business yet; [] = nothing is shared
     * with them. Only docs the client can see, only question threads.
     */
    documents?: MerchantDocument[];
}

export interface DecisionInput {
    decision: ClientDecision;
    answer?: string | null;
    /**
     * The handle from `uploadFile` for a provide_file answer, which the server
     * stores and presigns on read (`DecisionIn.file_key`). `fileName` alone is
     * the pre-T14 fallback: the name arrives, the file does not.
     */
    fileKey?: string | null;
    fileName?: string | null;
    comment?: string;
    by: string;
}

/** One answer in a batch: which item, and the decision without the shared `by`. */
export type DecisionBatchInput = Omit<DecisionInput, "by"> & { itemId: string };

/** What a batch of answers came to. `failed` keeps the server's sentence for each one that did not send. */
export interface DecisionBatchResult {
    ok: PackItem[];
    failed: Array<{ itemId: string; message: string }>;
}
export interface MessageInput {
    authorKind: Audience | "oura";
    authorName: string;
    body: string;
    /**
     * The handle from `uploadFile`, NOT a filename. The server stores this and
     * presigns it on read; a bare name was silently dropped, so an attachment
     * looked sent and arrived as nothing.
     */
    fileKey?: string | null;
    suggestedEdit?: { before: string; after: string } | null;
}
export interface PackDraft { title: string; intro: string; blocks: PackBlock[]; items: Array<Omit<PackItem, "id" | "packId" | "version" | "decision" | "decidedAt" | "decidedBy" | "answer" | "fileName" | "waitingOn">> }

// ---------------------------------------------------------------------------
// The approval rule. Pure, so the table shows it without a round trip and the
// BE can run the identical rule when it builds the plan.
// ---------------------------------------------------------------------------

const VISIBLE_WORK = /\b(page|pages|page copy|site copy|copy change|copywriting|paragraph|headline|title|description|publish|write|rewrite|draft|google business|profile|hours|logo|photo|image|price|prices|menu|service|services|faq)\b/i;
const TECHNICAL_WORK = /\b(robots|sitemap|crawl|redirect|canonical|schema|structured data|speed|compress|search console|index|indexing|broken link|link graph|internal link|analytics|verify|verification)\b/i;

export function suggestApproval(task: { action: string; notes?: string; owner: string }): ApprovalRequirement {
    if (task.owner === "client") return "required";
    const text = `${task.action} ${task.notes ?? ""}`;
    if (TECHNICAL_WORK.test(text) && !/\b(page copy|rewrite|new page)\b/i.test(text)) return "not_required";
    return VISIBLE_WORK.test(text) ? "required" : "not_required";
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

async function get<T>(path: string): Promise<T> {
    const res = await axios.get(`${AEO_BASE}${path}`, { headers: authHeaders() });
    return res.data.data as T;
}
async function post<T>(path: string, body: unknown): Promise<T> {
    const res = await axios.post(`${AEO_BASE}${path}`, body, { headers: authHeaders() });
    return res.data.data as T;
}
async function patch_<T>(path: string, body: unknown): Promise<T> {
    const res = await axios.patch(`${AEO_BASE}${path}`, body, { headers: authHeaders() });
    return res.data.data as T;
}
async function put_<T>(path: string, body: unknown): Promise<T> {
    const res = await axios.put(`${AEO_BASE}${path}`, body, { headers: authHeaders() });
    return res.data.data as T;
}
const enc = encodeURIComponent;

// Staff
export const listTaskApprovals = (b: string) => useFixtures ? mock.listTaskApprovals(b) : get<ApprovalOverride[]>(`/businesses/${enc(b)}/task-approvals`);
export const setTaskApproval = (b: string, taskId: string, requirement: ApprovalRequirement, showToClient = true, why?: ApprovalOverrideReason) =>
    // PUT, not PATCH: the server defines this as a whole-value replacement
    // (aeo_client_review.py). PATCH answers 405 and the override silently never
    // saved. `why` (H20a) is sent only when given, so the packs' own calls are unchanged.
    useFixtures ? mock.setTaskApproval(b, taskId, requirement, showToClient, why) : put_<ApprovalOverride>(`/businesses/${enc(b)}/task-approvals/${enc(taskId)}`, { requirement, showToClient, ...why });
export const listPacks = (b: string) => useFixtures ? mock.listPacks(b) : get<Pack[]>(`/businesses/${enc(b)}/packs`);
export const createPack = (b: string, draft: PackDraft) => useFixtures ? mock.createPack(b, draft) : post<Pack>(`/businesses/${enc(b)}/packs`, draft);
export const updatePack = (id: string, draft: Partial<PackDraft>, by: string) => useFixtures ? mock.updatePack(id, draft, by) : patch_<Pack>(`/packs/${enc(id)}`, draft);
export const publishPack = (id: string) => useFixtures ? mock.publishPack(id) : post<Pack>(`/packs/${enc(id)}/publish`, {});
export const listCallRequests = (b: string) => useFixtures ? mock.listCallRequests(b) : get<CallRequest[]>(`/businesses/${enc(b)}/call-requests`);
export const updateCallRequest = (id: string, status: CallRequest["status"]) => useFixtures ? mock.updateCallRequest(id, status) : patch_<CallRequest>(`/call-requests/${enc(id)}`, { status });
export const acceptSuggestedEdit = (itemId: string, messageId: string, by: string) => useFixtures ? mock.acceptSuggestedEdit(itemId, messageId, by) : post<PackItem>(`/items/${enc(itemId)}/accept-edit`, { messageId });

// The plan record (staff edit, both read)
/**
 * One file for a thread, an evidence block or a decision.
 *
 * Throws rather than returning a union: every caller here is inside a user
 * action that already has somewhere to put an error, and a half-uploaded file
 * has no useful partial state to report.
 */
export async function uploadFile(b: string, file: File): Promise<UploadedFile> {
    if (useFixtures) return mock.uploadFile(file);
    const form = new FormData();
    form.append("file", file);
    const token = getCookie("authToken");
    const res = await axios.post(`${AEO_BASE}/businesses/${enc(b)}/uploads`, form, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 120_000,
    });
    return res.data.data as UploadedFile;
}

/** At most this many headings on an upload, and this long each (the server's own limits). */
export const REPORT_SECTIONS_MAX = 12;
export const REPORT_SECTION_MAX_CHARS = 80;

/**
 * The staff report a merchant can open from their Reports tab (backend T12).
 *
 * `sections` are the headings inside it (#5112), sent as a JSON array. They
 * were never sent, so every uploaded report stored `sections: null` and only a
 * Snapshot report ever showed "Inside" (#4026 C-FE-8). Empty is left OFF the
 * form rather than sent as `[]`: absent stores NULL, which is what lets the
 * server fall back to the PDF's own bookmarks (C-BE-2).
 */
export async function uploadReport(b: string, file: File, title: string, summary = "", sections: string[] = [], notify = true): Promise<void> {
    if (useFixtures) return mock.uploadReport();
    const form = new FormData();
    form.append("file", file);
    form.append("title", title);
    form.append("summary", summary);
    // `notify=false` (#5695): no bell or email for this upload. The results card's two
    // documents go to the client in one email instead (ourteam-frontend#4398).
    if (!notify) form.append("notify", "false");
    const headings = sections.map((x) => x.trim()).filter(Boolean);
    if (headings.length > 0) form.append("sections", JSON.stringify(headings));
    const token = getCookie("authToken");
    await axios.post(`${AEO_BASE}/businesses/${enc(b)}/reports`, form, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 120_000,
    });
}

/** The signed-in API path an activity attachment is served at (ourteam-backend#4573). */
const ATTACHMENT_PATH = "/api/v1/activity/attachments/";

/** True for an evidence link that is an attachment, which only `fetchActivityAttachment` can read. */
export const isActivityAttachment = (url: string): boolean => url.startsWith(ATTACHMENT_PATH);

/**
 * One activity attachment's bytes. The route asks who the caller is on every
 * read, so a plain link, which carries no Authorization header, is refused:
 * the file is fetched with the header and handed back for a blob URL.
 */
export async function fetchActivityAttachment(path: string): Promise<Blob> {
    if (useFixtures) return mock.activityAttachment(path);
    const res = await axios.get<Blob>(`${apiURL}${path}`, { headers: authHeaders(), responseType: "blob", timeout: 60_000 });
    return res.data;
}

export async function createClientUser(b: string, input: { name: string; email: string }): Promise<CreateClientUserResult> {
    if (useFixtures) return mock.createClientUser(input);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${enc(b)}/client-users`, input, { headers: authHeaders(), timeout: 30_000 });
        return { ok: true, email: (res.data?.data?.email as string) || input.email };
    } catch (err) {
        const e = err as { response?: { status?: number; data?: { message?: string } } };
        if (e.response?.status === 409) {
            return { ok: false, kind: "conflict", message: e.response?.data?.message || "That email already belongs to another workspace" };
        }
        catchErrorLog(`[oura] createClientUser failed for ${b}: ${err}`);
        return { ok: false, kind: "error", message: e.response?.data?.message || "The login could not be created." };
    }
}

/** The last three, newest first — what the staff card shows under the textarea. */
export const listClientNotes = (b: string) => useFixtures ? mock.listClientNotes(b) : get<ClientNote[]>(`/businesses/${enc(b)}/client-notes`);
export const postClientNote = (b: string, body: string) => useFixtures ? mock.postClientNote(b, body) : post<ClientNote>(`/businesses/${enc(b)}/client-note`, { body });

export const getEngagement = (b: string) => useFixtures ? mock.getEngagement(b) : get<Engagement>(`/businesses/${enc(b)}/engagement`);
/**
 * The plan, or null only when the business truly has none (a 404). Any other
 * failure throws: a read that failed is not "no plan", and treating it as one
 * would offer to start onboarding a client who is already set up.
 */
export async function getEngagementIfExists(b: string): Promise<Engagement | null> {
    try {
        return await getEngagement(b);
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) return null;
        throw error;
    }
}

/**
 * The timeline as it stands: what is published, what is computed now, and what
 * would move. Null when the business has no engagement — a 404 there is the
 * design (nothing has been sold yet), not a failure worth a Sentry line.
 */
/**
 * Freeze the computed schedule as the next version (ourteam-backend#4821).
 *
 * `published: false` with `datesMoved: 0` is a SUCCESS, not a failure: nothing
 * moved, so no version was written and nobody was told. That is the whole point
 * of the ticket — a version that bumps on every save trains a merchant to
 * ignore the notification exactly when it matters.
 *
 * A refusal resolves rather than throws, because both refusals are answers the
 * reviewer has to read: `timeline_has_conflicts` means resolve them first, and
 * `timeline_not_computable` means there is no start date yet.
 */
/**
 * A refusal the screen renders, not a throw. `code` is the server's own
 * (`timeline_has_conflicts`, `timeline_not_computable`) and `message` is the
 * sentence it wrote — shown verbatim, because the server knows why it refused
 * and has already phrased it for a person.
 */
export interface TimelineFailure { code: string | null; message: string }

export interface PublishResult {
    published: boolean;
    datesMoved: number;
    version: number;
    message?: string;
}

/**
 * `notify: false` publishes quietly (#4503, ourteam-backend#6309): no bell, no
 * email, no "Dates moved" banner for the client. Only an explicit false is
 * quiet; anything else sends the request exactly as before.
 */
export async function publishTimeline(b: string, opts?: { notify?: boolean }): Promise<PublishResult | TimelineFailure> {
    // The fixture has no bell or email to hold back, so a quiet publish is the same publish there.
    if (useFixtures) return mock.publishTimeline(b);
    try {
        return await post<PublishResult>(`/businesses/${enc(b)}/timeline/publish${opts?.notify === false ? "?notify=false" : ""}`, {});
    } catch (err) {
        const e = err as { response?: { data?: { message?: string; code?: string } } };
        const message = e.response?.data?.message;
        if (!message) catchErrorLog(`[oura] publish timeline ${b} failed: ${err}`);
        return {
            code: e.response?.data?.code ?? null,
            message: message || "The timeline could not be published, and it is not clear what the server did.",
        };
    }
}

/** A refusal rather than a published version. */
export const isTimelineFailure = (r: PublishResult | TimelineFailure): r is TimelineFailure =>
    typeof (r as TimelineFailure).message === "string" && !("published" in r);

export async function getTimeline(b: string): Promise<TimelinePayload | null> {
    if (useFixtures) return mock.getTimeline(b);
    try { return await get<TimelinePayload>(`/businesses/${enc(b)}/timeline`); }
    catch { return null; }
}
export const seedEngagement = (b: string, e: Omit<Engagement, "changeLog">) => useFixtures ? mock.seedEngagement(b, e) : post<Engagement>(`/businesses/${enc(b)}/engagement`, e);

/**
 * Activate a business: one request, all or none.
 *
 * Resolves to the 201 body, or to an `ActivationFailure` carrying the server's
 * own code and sentence. It never throws for a refusal the staffer can act on —
 * a 409 "this business already has an engagement" is an ANSWER, and the screen
 * has to show it on the step the staffer is standing on.
 */
export async function activateBusiness(b: string, input: ActivationInput): Promise<ActivationResult | ActivationFailure> {
    if (useFixtures) return mock.activateBusiness(b, input);
    try {
        return await post<ActivationResult>(`/businesses/${enc(b)}/activate`, input);
    } catch (err) {
        const e = err as { response?: { data?: { message?: string; code?: string } } };
        const message = e.response?.data?.message;
        if (!message) catchErrorLog(`[oura] activate ${b} failed: ${err}`);
        return {
            code: e.response?.data?.code ?? null,
            // NOT a cheerful generic. The server's failure text says explicitly
            // whether anything was written ("Activation failed and nothing was
            // written"), and that is the one thing a staffer needs to know
            // before they try again.
            message: message || "Activation could not be completed, and it is not clear what the server did. Check the business before trying again.",
        };
    }
}

/** `ActivationFailure` rather than a result. */
export const isActivationFailure = (r: ActivationResult | ActivationFailure): r is ActivationFailure =>
    typeof (r as ActivationFailure).message === "string";
/**
 * The PATCH takes its lines in the seed's shape: a line read without
 * `covers` / `reported_by_hand` (a row from before #4814) goes back without
 * them, and the server authors them from its catalogue, as it does on read.
 */
export type EngagementPatch = Partial<Pick<Engagement, "startedAt" | "planName" | "termMonths" | "pageUnits" | "skipPublicHolidays">> & {
    /**
     * How many outlets the plan covers, 1 to 50 (ourteam-backend#6407). A change
     * MUST come with `outletNames` for the new count in the same PATCH
     * (`outlet_names_required`); outlets are matched by position, so the
     * outlet in row 1 keeps what was ticked for it.
     */
    outlets?: number;
    deliverables?: DeliverableSeed[];
    /**
     * Merged with what is already there by the CALLER, never replaced blind:
     * the server stores what it is sent, and a bare `{priority_keywords}`
     * would drop the baseline and `source_assessment_id` that live beside it.
     */
    tracking?: EngagementTracking;
    /**
     * The outlets' names, as PLAIN STRINGS in contract order — not the
     * `{slug, name}` shape they are read back in. The server must receive
     * EXACTLY `outlets` of them (`outlet_names_count`), each non-empty, at most
     * 80 characters and unique case-insensitively (`outlet_names_invalid`).
     *
     * Sending them is what confirms them: the server stamps
     * `outletNamesConfirmedAt`, and until it has, every per-outlet tick on a
     * contract with 2 or more outlets is refused `outlets_not_confirmed`.
     *
     * An entry can instead be `{name, address?, phone?}` to set the outlet's
     * address and phone as on the quote (ourteam-backend#5808): only the keys
     * sent change, null clears one, and a plain name keeps both.
     */
    outletNames?: (string | OutletNameSent)[];
    /** The `updatedAt` this edit was made from: 409 `engagement_changed` when someone saved in between (H27). */
    expectedUpdatedAt?: string | null;
};
export const updateEngagement = (b: string, patch: EngagementPatch, by: string, summary: string) =>
    useFixtures ? mock.updateEngagement(b, patch, by, summary) : patch_<Engagement>(`/businesses/${enc(b)}/engagement`, { ...patch, summary });

// Merchant
/**
 * The merchant's own business.
 *
 * Read out of the client page rather than from `GET /businesses/mine`, which
 * does exist (aeo_business.py) and returns the one business owned by the
 * caller's workspace. Two reasons, in order:
 *
 *   1. The client page is one round trip the screen makes anyway, and it
 *      already contains the business. A second call for a field we are holding
 *      buys nothing.
 *   2. `/businesses/mine` 404s for a staff or service caller BY DESIGN — such a
 *      session owns no single business. A staffer previewing the client view is
 *      exactly that caller, so the route would answer 404 for the case we most
 *      often test, and callers that swallow the 404 would leave the merchant's
 *      id null and every client screen empty.
 *
 * An earlier version of this comment claimed the route did not exist at all.
 * That was a wrong reading of point 2 — the 404 was the design, not absence.
 */
export const getMyBusiness = async (): Promise<MyBusiness | null> => {
    if (useFixtures) return mock.getMyBusiness();
    const r = await getClientPage();
    return r.ok ? r.payload.business : null;
};

/**
 * Why the read failed, not just that it did.
 *
 * `null` could not tell "still loading" from "the server said no", so a 403
 * rendered as a spinner that never stopped: the merchant screens sat blank on
 * staging the moment the live flag went on, because a staffer previewing as a
 * merchant is a staffer to the API and `/businesses/mine/...` is 403 for staff
 * by design. The screen can only say something useful if it is told which.
 */
export type ClientPageResult =
    | { ok: true; payload: ClientPagePayload }
    | { ok: false; reason: "forbidden" | "notFound" | "unauthorised" | "network"; status: number | null };

/**
 * One page load asked the server for the whole client page three times.
 *
 * The rail's bell wants the business id, the workspace wants the id, and the
 * screen wants the payload — and since `getMyBusiness` reads the id out of the
 * client page (there is no `GET /businesses/mine`), all three became a full
 * fetch of business, stats, packs, threads and engagement. Measured on staging:
 * three identical `GET /businesses/mine/client-page` per navigation.
 *
 * So callers share one request. An in-flight call is handed to whoever asks
 * next, and the settled result is reused for a few seconds — long enough to
 * cover the burst as a route mounts, short enough that a merchant acting on
 * their page still sees the effect on the next screen. `refreshClientPage()`
 * drops it for the cases that must not wait, and every write path already
 * refetches through its own call.
 */
const CLIENT_PAGE_TTL_MS = 5_000;
let clientPageInFlight: Promise<ClientPageResult> | null = null;
let clientPageCached: { at: number; result: ClientPageResult } | null = null;

/**
 * Who wants to hear that the client page changed.
 *
 * The shell's Decisions badge reads the same payload as the screen, but it is
 * mounted once and never remounts, so without a nudge it keeps the count from
 * before the merchant answered. Every write path already ends in
 * `invalidateClientPage()`, which makes that the one place to tell them.
 */
const clientPageListeners = new Set<() => void>();

/**
 * Be told whenever the shared client page is invalidated, so a long-lived
 * reader (the rail badge) can read it again. Returns the unsubscribe.
 */
export function subscribeClientPage(cb: () => void): () => void {
    clientPageListeners.add(cb);
    return () => { clientPageListeners.delete(cb); };
}

/** Forget the shared client page, so the next read goes to the server, and tell every subscriber. */
export function invalidateClientPage(): void {
    clientPageInFlight = null;
    clientPageCached = null;
    // A copy, so a listener that unsubscribes while being told does not skip
    // the one after it.
    Array.from(clientPageListeners).forEach((cb) => cb());
}

export function getClientPage(): Promise<ClientPageResult> {
    if (clientPageInFlight) return clientPageInFlight;
    if (clientPageCached && Date.now() - clientPageCached.at < CLIENT_PAGE_TTL_MS) {
        return Promise.resolve(clientPageCached.result);
    }
    // Only the latest read may settle the shared state: a read that an
    // invalidation overtook would otherwise cache the older page, and drop
    // the pointer to the newer read while it is still on its way.
    const run: Promise<ClientPageResult> = fetchClientPage().then(
        (result) => {
            // A failure is cached too, briefly: three components hitting the
            // same 403 in a row should ask once, not three times.
            if (clientPageInFlight === run) {
                clientPageCached = { at: Date.now(), result };
                clientPageInFlight = null;
            }
            return result;
        },
        (err) => {
            if (clientPageInFlight === run) clientPageInFlight = null;
            throw err;
        },
    );
    clientPageInFlight = run;
    return run;
}

const MERCHANT_DOC_STATES: ReadonlySet<string> = new Set(["needs_your_reply", "with_us", "approved", "nothing_asked"]);
const FILE_TYPES: ReadonlySet<string> = new Set(["gdoc", "word", "pdf", "sheet", "other"]);
const QUESTION_KINDS: ReadonlySet<string> = new Set<QuestionKind>(["agree", "change", "choice", "text", "file"]);
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/**
 * One shared doc as the merchant screens can rely on it. Route 12 is new: a
 * row with a null title or no `openQuestions` would throw in the one provider
 * every merchant screen sits under, and take them all down. A missing title
 * falls back to "A document"; missing lists are empty; a state this build
 * does not know reads as nothing to answer. A row with no id is dropped.
 */
export function normaliseMerchantDocument(raw: unknown): MerchantDocument | null {
    if (!raw || typeof raw !== "object") return null;
    const d = raw as Record<string, unknown>;
    const id = str(d.id);
    if (!id) return null;
    const q = d.questions && typeof d.questions === "object" ? (d.questions as Record<string, unknown>) : null;
    const openQuestions = Array.isArray(d.openQuestions)
        ? d.openQuestions.flatMap((x) => {
            if (!x || typeof x !== "object") return [];
            const o = x as Record<string, unknown>;
            const qid = str(o.id);
            const text = str(o.text);
            if (!qid || text === null) return [];
            // B26's record rides along, so the answer box fits the question (a file-only one asks for the file).
            const kind = str(o.kind);
            return [{
                id: qid,
                text,
                askedAt: str(o.askedAt) ?? "",
                code: str(o.code),
                questionId: str(o.questionId),
                kind: kind && QUESTION_KINDS.has(kind) ? (kind as QuestionKind) : null,
                number: num(o.number),
                options: Array.isArray(o.options) ? o.options.filter((x): x is string => typeof x === "string") : [],
            }];
        })
        : [];
    const state = str(d.state);
    const fileType = str(d.fileType);
    return {
        id,
        title: str(d.title)?.trim() || "A document",
        // Kept, not dropped with the fields this list does not name: the Shared docs list names a doc by its
        // file (ourteam-backend#6582), and a custom doc type's name shows as it is (ourteam-backend#6520).
        fileTitle: str(d.fileTitle)?.trim() || null,
        custom: d.custom === true,
        codes: Array.isArray(d.codes) ? d.codes.filter((c): c is string => typeof c === "string") : [],
        outlet: str(d.outlet),
        fileType: (fileType && FILE_TYPES.has(fileType) ? fileType : "other") as MerchantDocument["fileType"],
        url: str(d.url) ?? "",
        sharedAt: str(d.sharedAt),
        replyBy: str(d.replyBy),
        late: typeof d.late === "boolean" ? d.late : null,
        lateWorkingDays: num(d.lateWorkingDays),
        state: (state && MERCHANT_DOC_STATES.has(state) ? state : "nothing_asked") as MerchantDocument["state"],
        questions: q ? { out: num(q.out) ?? 0, waiting: num(q.waiting) ?? 0, approved: num(q.approved) ?? 0 } : null,
        openQuestions,
        updatedAt: str(d.updatedAt),
    };
}

/** The client page with its `documents` made safe (absent stays absent: Oura does not read docs there). */
function normaliseClientPage(served: ClientPagePayload): ClientPagePayload {
    // A plan line's name without the quotation's count ("Articles published (2–3)", 27 Oct 2026).
    const items = served.plan?.items;
    const inHand = served.noDecisionNeeded;
    const payload = {
        ...served,
        ...(items?.some((i) => lineLabel(i.label) !== i.label) ? { plan: { ...served.plan!, items: items.map((i) => ({ ...i, label: lineLabel(i.label) })) } } : {}),
        ...(inHand?.some((i) => lineLabel(i.label) !== i.label) ? { noDecisionNeeded: inHand.map((i) => ({ ...i, label: lineLabel(i.label) })) } : {}),
    };
    const docs: unknown = (payload as { documents?: unknown }).documents;
    if (docs === undefined || docs === null) return docs === null ? { ...payload, documents: undefined } : payload;
    const list = Array.isArray(docs) ? docs.map(normaliseMerchantDocument).filter((d): d is MerchantDocument => d !== null) : [];
    return { ...payload, documents: list };
}

async function fetchClientPage(): Promise<ClientPageResult> {
    if (useFixtures) {
        // The docs "error" switch fails the merchant's page too, so a failed documents read can be seen there:
        // a first load shows the page's own failure, a later one keeps the list under "Could not refresh".
        if (isDocsSample && docsDevMode() === "error") {
            await delay(null, docsMockDelayMs());
            return { ok: false, reason: "network", status: null };
        }
        const payload = await mock.getClientPage();
        return payload ? { ok: true, payload } : { ok: false, reason: "notFound", status: null };
    }
    try {
        return { ok: true, payload: normaliseClientPage(await get<ClientPagePayload>(`/businesses/mine/client-page`)) };
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status ?? null;
        const reason = status === 403 ? "forbidden" : status === 404 ? "notFound" : status === 401 ? "unauthorised" : "network";
        // A 403 here is an expected shape (staff on a merchant route), not an
        // incident; only report the ones nobody predicted.
        if (reason === "network" || reason === "notFound") catchErrorLog(`[oura] client page fetch failed (${status ?? "no response"})`);
        return { ok: false, reason, status };
    }
}
/**
 * The same page, addressed by business, for a staffer previewing what the
 * merchant sees (#4768). Read-only: every write on it is the merchant's, and
 * `decide` answers 403 to a staff token.
 *
 * Not shared with the cache above. That cache is the merchant's own page,
 * keyed by nobody because there is only one; this one is keyed by business and
 * read once per preview.
 */
export async function getClientPageFor(businessId: string): Promise<ClientPageResult> {
    if (useFixtures) {
        const payload = await mock.getClientPageFor(businessId);
        return payload ? { ok: true, payload } : { ok: false, reason: "notFound", status: null };
    }
    try {
        return { ok: true, payload: normaliseClientPage(await get<ClientPagePayload>(`/businesses/${enc(businessId)}/client-page`)) };
    } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status ?? null;
        const reason = status === 403 ? "forbidden" : status === 404 ? "notFound" : status === 401 ? "unauthorised" : "network";
        if (reason === "network") catchErrorLog(`[oura] client page preview for ${businessId} failed (${status ?? "no response"})`);
        return { ok: false, reason, status };
    }
}

/**
 * The reports a business has been sent, newest first, for the staff side.
 *
 * There is NO dedicated reports route: `POST /businesses/{id}/reports` stores
 * one and answers nothing, and nothing lists them. The reports do come back on
 * the client page, where `_reports` merges the engagement's documents, every
 * completed assessment's rendered report and the staff-uploaded PDFs — and
 * `GET /businesses/{id}/client-page` is staff-readable (#4768). So the list
 * staff need already exists; it was only never shown to them (#4009).
 *
 * `failed` is a read that did not land, which is not an empty list: a client
 * with no reports and a read that fell over must not look the same. A business
 * with no client page (nothing sold yet) has no reports and no failure.
 */
export async function listBusinessReports(businessId: string): Promise<{ reports: ClientReportLink[]; failed: boolean }> {
    const r = await getClientPageFor(businessId);
    if (r.ok) return { reports: r.payload.reports ?? [], failed: false };
    // A 404 is the answer "no page, so no reports". Anything else is a failure.
    return { reports: [], failed: r.reason !== "notFound" };
}

/**
 * The client has opened a report (ourteam-backend#5414 C-BE-1).
 *
 * CLIENT ONLY -- the server answers 403 to anyone else, and staff reading a
 * report must never mark it read on the client's behalf, or "not opened yet"
 * stops meaning anything.
 *
 * Never throws and answers nothing useful on failure: this is a side effect of
 * opening a drawer, and a client who opened their report does not need to be
 * told that recording it failed. The badge corrects itself on the next read.
 */
export async function markReportRead(reportId: string): Promise<number | null> {
    if (useFixtures) return null;
    try {
        const r = await post<{ unreadReports?: number }>(`/businesses/mine/reports/${enc(reportId)}/read`, {});
        // The nav badge is read from the cached client page, so the cache has
        // to be told: without this the count sat there until it expired, and
        // the client had marked a report read with the badge still claiming
        // otherwise.
        invalidateClientPage();
        return typeof r?.unreadReports === "number" ? r.unreadReports : null;
    } catch {
        return null;
    }
}

/**
 * A shared document opened by the CLIENT (ourteam-backend#6108): View on the
 * Documents tab, or Open in its drawer. The same rules as markReportRead:
 * client only (staff get a 403, and must never mark it for them), never
 * throws, and the cached client page is told so the counts refresh.
 * Resolves true once the server has the read, false when the POST failed, so
 * the screen clears a "New" mark only for a read that was kept.
 */
export async function markDocumentRead(documentId: string): Promise<boolean> {
    if (useFixtures) return true;
    try {
        await post<{ id: string; readAt: string; unreadDocuments: number }>(`/businesses/mine/documents/${enc(documentId)}/read`, {});
        invalidateClientPage();
        return true;
    } catch {
        return false;
    }
}

/**
 * Every report read at once, for the client opening the Reports page.
 *
 * The tester asked for the sidebar count to go once they had been to the page,
 * which is what this is: the page IS the acknowledgement. The rows keep the
 * "New" tag they had when the page loaded, so the visit still shows which ones
 * were new -- it is only the next load that is clean.
 */
export async function markAllReportsRead(): Promise<number | null> {
    if (useFixtures) return null;
    try {
        const r = await post<{ marked?: number; unreadReports?: number }>(`/businesses/mine/reports/read-all`, {});
        invalidateClientPage();
        return typeof r?.unreadReports === "number" ? r.unreadReports : null;
    } catch {
        return null;
    }
}

export const decideItem = (itemId: string, input: DecisionInput) => useFixtures ? mock.decideItem(itemId, input) : post<PackItem>(`/items/${enc(itemId)}/decide`, input);

/** The server's sentence for a decision it refused, or ours when it sent none. */
function decisionFailure(itemId: string, err: unknown): string {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
    catchErrorLog(`[oura] decide ${itemId} failed: ${err}`);
    return "This answer did not send. Try again.";
}

/**
 * Send several answers at once ("Send 3 answers").
 *
 * There is no batch route yet, so this is the single decide call in a loop.
 * SEQUENTIAL, not parallel: the server marks a pack submitted when its last
 * pending item is decided, and two answers from one pack landing together
 * would race that check. One failure does not stop the rest; it comes back in
 * `failed` with the reason, so the screen keeps that draft and says why.
 *
 * Leaves the shared client page alone: every caller reads it again fresh
 * when anything was sent (the workspace's refresh invalidates it, which also
 * re-reads the rail's Decisions badge). Invalidating here as well started a
 * second full read of the page for every answer.
 *
 * Costs one bell row and one email to the account manager per answer until
 * the batch route lands. BE handoff: "Send several answers in one call".
 */
export async function decideMany(inputs: DecisionBatchInput[], by: string): Promise<DecisionBatchResult> {
    const result: DecisionBatchResult = { ok: [], failed: [] };
    for (const { itemId, ...input } of inputs) {
        try {
            result.ok.push(await decideItem(itemId, { ...input, by }));
        } catch (err) {
            result.failed.push({ itemId, message: decisionFailure(itemId, err) });
        }
    }
    return result;
}
export const requestCall = (b: string, input: { itemId: string | null; requestedBy: string; preference: string; note: string }) =>
    // Created on the merchant's own path — only a client asks for a call, so the
    // server takes the business from the token. `/businesses/{id}/call-requests`
    // is GET-only and answered 405 to this.
    useFixtures ? mock.requestCall(b, input) : post<CallRequest>(`/businesses/mine/call-requests`, input);

/**
 * The merchant's change request route is not built yet (BE handoff "Merchant
 * change request"). Explicit `=== "true"`, absent means OFF, the rule every
 * Oura flag follows: a control that can only fail is worse than none.
 */
export const isChangeRequestLive = (): boolean => import.meta.env.VITE_ENABLE_OURA_CHANGE_REQUEST === "true";
/** Whether "Ask for a change" shows: always on the mock store, on live only behind the flag. */
export const canRequestChange = (): boolean => useFixtures || isChangeRequestLive();
/**
 * A change request call the server refused, or that never reached it. `code`
 * is the server's own (note_required, note_too_long, file_not_yours,
 * reply_required, small_change_already_filed) or null for a failure with no
 * code; `existingId` names this month's small change on a 409.
 */
export class ChangeRequestError extends Error {
    readonly code: DocsErrorCode | null;
    readonly status: number | null;
    readonly existingId: string | null;
    constructor(message: string, opts: { code?: DocsErrorCode | null; status?: number | null; existingId?: string | null } = {}) {
        super(message);
        this.name = "ChangeRequestError";
        this.code = opts.code ?? null;
        this.status = opts.status ?? null;
        this.existingId = opts.existingId ?? null;
    }
}

function changeError(err: unknown, what: string): ChangeRequestError {
    if (err instanceof ChangeRequestError) return err;
    const f = readDocsError(err, what);
    return new ChangeRequestError(f.message, { code: f.code, status: f.status, existingId: f.existingId });
}

/**
 * Fills the keys an older server leaves out, so a screen can read every
 * field of the contract shape. The route that shipped first (the Overview
 * handoff) answered only id, businessId, note, requestedBy, status and at.
 */
export function normaliseChangeRequest(raw: Partial<ChangeRequest> & Pick<ChangeRequest, "id">): ChangeRequest {
    const status: RequestStatus = raw.status === "done" || raw.status === "answered" ? raw.status : "open";
    return {
        id: raw.id,
        businessId: raw.businessId ?? "",
        requestedBy: raw.requestedBy ?? "",
        requesterEmail: raw.requesterEmail ?? null,
        note: raw.note ?? "",
        files: Array.isArray(raw.files) ? raw.files : [],
        status,
        reply: raw.reply ? { ...raw.reply, files: replyFiles(raw.reply.files) } : null,
        replies: Array.isArray(raw.replies) ? raw.replies.map((r) => ({ ...r, files: replyFiles(r.files) })) : undefined,
        smallChange: raw.smallChange ?? null,
        at: raw.at ?? "",
        doneAt: raw.doneAt ?? null,
        doneBy: raw.doneBy ?? null,
        // What Oura read out of the message (#6276): kept when the server sends it.
        title: typeof raw.title === "string" ? raw.title : null,
        where: typeof raw.where === "string" ? raw.where : null,
        why: typeof raw.why === "string" ? raw.why : null,
        checklist: lines(raw.checklist),
        notes: lines(raw.notes),
        source: raw.source,
        original: raw.original ?? null,
    };
}

/** A reply's screenshots, as the server sent them: each needs a name and a link. */
/**
 * Every staff reply on a request, oldest first (#6874). An older server sends
 * only the newest, as `reply`: then that one alone.
 */
export function everyReply<R>(c: { reply: R | null; replies?: R[] }): R[] {
    if (c.replies && c.replies.length > 0) return c.replies;
    return c.reply ? [c.reply] : [];
}

export function replyFiles(v: unknown): ReplyFile[] {
    if (!Array.isArray(v)) return [];
    return v.flatMap((f) => {
        const o = f as { fileKey?: unknown; name?: unknown; url?: unknown } | null;
        if (!o || typeof o.name !== "string" || !o.name || typeof o.url !== "string" || !o.url) return [];
        return [{ ...(typeof o.fileKey === "string" ? { fileKey: o.fileKey } : {}), name: o.name, url: o.url }];
    });
}

/** A list of lines from the server, strings only. */
function lines(v: unknown): string[] {
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "") : [];
}

/**
 * On the merchant's own path, like a call request: the server takes the
 * business from the token. `fileKeys` come from `uploadFile`. Throws a
 * ChangeRequestError (note_required, note_too_long, file_not_yours).
 */
export async function requestChange(input: { note: string; requestedBy: string; fileKeys?: string[] }): Promise<ChangeRequest> {
    let row: ChangeRequest;
    if (useFixtures) row = await mock.requestChange(input);
    else {
        try {
            const body = input.fileKeys?.length ? input : { note: input.note, requestedBy: input.requestedBy };
            row = normaliseChangeRequest(await post<ChangeRequest>(`/businesses/mine/change-requests`, body));
        } catch (err) {
            throw changeError(err, "request change");
        }
    }
    // "Your requests" shows it at once, before its next read.
    const mine = toMyChangeRequest(row);
    Array.from(myChangeListeners).forEach((cb) => cb(mine));
    return row;
}

/* The merchant's own requests ("Your requests" on Work). */

/** Who wants to hear that the merchant just sent a request. */
const myChangeListeners = new Set<(row: MyChangeRequest) => void>();

/** Be told the moment this tab sends a request, with the row the server answered. Returns the unsubscribe. */
export function subscribeMyChangeRequests(cb: (row: MyChangeRequest) => void): () => void {
    myChangeListeners.add(cb);
    return () => {
        myChangeListeners.delete(cb);
    };
}

/** The calendar month now in Malaysia, "2026-10": the month a small change counts in (YW, 29 Sep 2026). */
export function malaysianMonthNow(now: number = Date.now()): string {
    return new Date(now + 8 * 3_600_000).toISOString().slice(0, 7);
}

/**
 * The merchant's view of a request, from either shape: route 18's own, or
 * the full ChangeRequest (the sample store, and the row route 13 answers).
 * Whatever only staff need is dropped here, so no screen can show it.
 */
type RawMyChangeRequest = { id: string } & Partial<Pick<MyChangeRequest, "requestedBy" | "note" | "status" | "at" | "doneAt">> & {
    files?: Array<{ name?: unknown; url?: unknown } | null> | null;
    reply?: { body?: unknown; by?: unknown; at?: unknown; files?: unknown } | null;
    replies?: Array<{ body?: unknown; by?: unknown; at?: unknown; files?: unknown } | null> | null;
    smallChange?: { month?: unknown; filedAt?: unknown } | null;
};

export function toMyChangeRequest(raw: RawMyChangeRequest): MyChangeRequest {
    const status: RequestStatus = raw.status === "done" || raw.status === "answered" ? raw.status : "open";
    const text = (v: unknown) => (typeof v === "string" ? v : "");
    const files = Array.isArray(raw.files) ? raw.files : [];
    const month = raw.smallChange?.month;
    return {
        id: raw.id,
        requestedBy: raw.requestedBy ?? "",
        note: raw.note ?? "",
        files: files.flatMap((f) => (f && typeof f.name === "string" && f.name ? [{ name: f.name, url: text(f.url) }] : [])),
        status,
        reply: raw.reply && typeof raw.reply.body === "string" ? { body: raw.reply.body, by: text(raw.reply.by), at: text(raw.reply.at), files: replyFiles(raw.reply.files) } : null,
        replies: (Array.isArray(raw.replies) ? raw.replies : []).flatMap((r) =>
            r && typeof r.body === "string" ? [{ body: r.body, by: text(r.by), at: text(r.at), files: replyFiles(r.files) }] : [],
        ),
        smallChange: typeof month === "string" && /^\d{4}-\d{2}$/.test(month) ? { month, filedAt: text(raw.smallChange?.filedAt) || null } : null,
        at: raw.at ?? "",
        doneAt: raw.doneAt ?? null,
    };
}

/**
 * Merchant: every request their business sent, newest first (route 18, GET
 * /businesses/mine/change-requests). The server takes the business from the
 * token. Throws a ChangeRequestError; `readMyChangeRequests` never throws.
 */
export async function listMyChangeRequests(): Promise<MyChangeRequest[]> {
    if (useFixtures) return mock.listMyChangeRequests();
    try {
        const rows = await get<RawMyChangeRequest[]>(`/businesses/mine/change-requests`);
        return (rows ?? []).map((r) => toMyChangeRequest(r)).sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0));
    } catch (err) {
        throw changeError(err, "list my change requests");
    }
}

/**
 * Merchant: the same list as a result. `notServed` when the route is not
 * live (the change-request switch is off, or the server answers 404 or 405),
 * so Work shows no list rather than an empty one.
 */
export async function readMyChangeRequests(): Promise<DocsResult<MyChangeRequest[]>> {
    if (!canRequestChange()) return docsFailure("notServed");
    try {
        return { ok: true, data: await listMyChangeRequests() };
    } catch (err) {
        if (err instanceof ChangeRequestError) {
            if (err.code) return docsFailure("rejected", { code: err.code, status: err.status, message: err.message });
            if (err.status === 404 || err.status === 405) return docsFailure("notServed", { status: err.status });
            if (err.status === 401) return docsFailure("unauthorised", { status: err.status });
            if (err.status === 403) return docsFailure("forbidden", { status: err.status });
        }
        return docsFailure("network");
    }
}

/** Route 19's row as it may arrive: every key but the id optional. */
type RawMyCallRequest = { id: string } & Partial<Record<"requestedBy" | "about" | "preference" | "note" | "status" | "at", unknown>>;

export function toMyCallRequest(raw: RawMyCallRequest): MyCallRequest {
    const text = (v: unknown) => (typeof v === "string" ? v : "");
    const status: CallRequest["status"] = raw.status === "scheduled" || raw.status === "closed" ? raw.status : "open";
    return {
        id: raw.id,
        requestedBy: text(raw.requestedBy),
        about: text(raw.about).trim() || null,
        preference: text(raw.preference),
        note: text(raw.note),
        status,
        at: text(raw.at),
    };
}

/**
 * Merchant: every call their business asked for, newest first, as a result
 * (route 19, GET /businesses/mine/call-requests). `notServed` where the
 * route is not live (the change-request switch is off, or the server answers
 * 404 or 405), so "Your requests" shows the changes alone, never "no calls".
 */
export async function readMyCallRequests(): Promise<DocsResult<MyCallRequest[]>> {
    if (!canRequestChange()) return docsFailure("notServed");
    try {
        const rows = useFixtures ? await mock.listMyCallRequests() : (await get<RawMyCallRequest[]>(`/businesses/mine/call-requests`)) ?? [];
        return { ok: true, data: rows.map(toMyCallRequest).sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0)) };
    } catch (err) {
        const status = err instanceof ChangeRequestError ? err.status : (err as { response?: { status?: number } } | null)?.response?.status ?? null;
        if (status === 404 || status === 405) return docsFailure("notServed", { status });
        if (status === 401) return docsFailure("unauthorised", { status });
        if (status === 403) return docsFailure("forbidden", { status });
        return docsFailure("network");
    }
}

/**
 * Staff: every change request for a business, newest first (route 15).
 *
 * Kept for the callers that read it inside a Promise.all: on live with the
 * change-request switch off the route does not exist, so it answers [] and
 * the rest of the Client tab still loads. A screen that must tell "nothing
 * asked" from "cannot be read yet" uses `readChangeRequests`.
 */
export const listChangeRequests = async (b: string): Promise<ChangeRequest[]> => {
    if (useFixtures) return mock.listChangeRequests(b);
    if (!isChangeRequestLive()) return [];
    try {
        const rows = await get<Array<Partial<ChangeRequest> & Pick<ChangeRequest, "id">>>(`/businesses/${enc(b)}/change-requests`);
        return (rows ?? []).map(normaliseChangeRequest);
    } catch (err) {
        throw changeError(err, "list change requests");
    }
};

/**
 * Staff: the same list as a result that never throws. `notServed` when the
 * route is not live yet, so the Requests list says it cannot be read yet
 * rather than "Nothing asked".
 */
export async function readChangeRequests(b: string): Promise<DocsResult<ChangeRequest[]>> {
    if (!useFixtures && !isChangeRequestLive()) return docsFailure("notServed");
    try {
        return { ok: true, data: await listChangeRequests(b) };
    } catch (err) {
        if (err instanceof ChangeRequestError) {
            if (err.code) return docsFailure("rejected", { code: err.code, status: err.status, message: err.message });
            if (err.status === 404 || err.status === 405) return docsFailure("notServed", { status: err.status });
            if (err.status === 401) return docsFailure("unauthorised", { status: err.status });
            if (err.status === 403) return docsFailure("forbidden", { status: err.status });
        }
        return docsFailure("network");
    }
}

/**
 * Staff: reply from oura@ (route 16). The server emails the requester, rings
 * their bell, and sets answered unless the request is done. Throws a
 * ChangeRequestError (reply_required).
 */
export async function replyToChangeRequest(id: string, body: string, fileKeys: string[] = []): Promise<ChangeRequest> {
    if (useFixtures) return mock.replyToChangeRequest(id, body, fileKeys);
    try {
        // `fileKeys` only when there are some: an older server is sent what it always was.
        return normaliseChangeRequest(await post<ChangeRequest>(`/change-requests/${enc(id)}/reply`, fileKeys.length > 0 ? { body, fileKeys } : { body }));
    } catch (err) {
        throw changeError(err, "reply to change request");
    }
}

/**
 * One screenshot for a staff reply, stored by the change-request upload route
 * (POST /change-requests/uploads: PNG, JPEG or WebP, up to 10 MB). Its key
 * goes in the reply's `fileKeys`.
 */
export async function uploadReplyPicture(file: File): Promise<string> {
    if (useFixtures) {
        // A data link, not a blob: the sample store keeps the reply, so it still opens after a reload and on the client's side.
        const url = await new Promise<string>((done, fail) => {
            const reader = new FileReader();
            reader.onload = () => done(String(reader.result));
            reader.onerror = () => fail(reader.error);
            reader.readAsDataURL(file);
        });
        const fileKey = `mock/reply/${Date.now()}-${file.name}`;
        mockUploads.set(fileKey, { name: file.name, url });
        return delay(fileKey, 200);
    }
    const form = new FormData();
    form.append("file", file, file.name);
    const token = getCookie("authToken");
    const res = await axios.post(`${AEO_BASE}/change-requests/uploads`, form, { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 120_000 });
    const key = res.data?.data?.fileKey;
    if (typeof key !== "string" || !key) throw new Error("upload returned no fileKey");
    return key;
}

/**
 * Staff: Done, reopen, or file as the month's small change (route 17).
 * Takes the old status string, or the contract's patch. Filing a second small
 * change in a month throws a ChangeRequestError with code
 * small_change_already_filed and `existingId`; send `confirmOverLimit: true`
 * to file it anyway.
 */
export async function updateChangeRequest(id: string, change: RequestStatus | ChangeRequestPatchIn): Promise<ChangeRequest> {
    const patch: ChangeRequestPatchIn = typeof change === "string" ? { status: change } : change;
    if (useFixtures) return mock.updateChangeRequest(id, patch);
    try {
        return normaliseChangeRequest(await patch_<ChangeRequest>(`/change-requests/${enc(id)}`, patch));
    } catch (err) {
        throw changeError(err, "update change request");
    }
}

// Both
export const listThreads = (b: string) => useFixtures ? mock.listThreads(b) : get<Thread[]>(`/businesses/${enc(b)}/threads`);
export const postMessage = (threadId: string, input: MessageInput) => useFixtures ? mock.postMessage(threadId, input) : post<Thread>(`/threads/${enc(threadId)}/messages`, input);
export const resolveThread = (threadId: string) => useFixtures ? mock.resolveThread(threadId) : post<Thread>(`/threads/${enc(threadId)}/resolve`, {});
export const listNotifications = (audience: Audience, b: string) => useFixtures ? mock.listNotifications(audience, b) : get<Notification[]>(`/notifications?audience=${audience}&business=${enc(b)}`);
export const markNotificationRead = (id: string) => useFixtures ? mock.markRead(id) : post<Notification>(`/notifications/${enc(id)}/read`, {});

// ---------------------------------------------------------------------------
// In-memory store, persisted to localStorage. DELETE once the endpoints exist.
// ---------------------------------------------------------------------------

interface Store {
    engagements: Record<string, Engagement>;
    approvals: Record<string, ApprovalOverride[]>;
    packs: Pack[];
    threads: Thread[];
    notifications: Notification[];
    calls: CallRequest[];
    /** Optional so a store persisted before notes existed still loads. */
    notes?: Record<string, ClientNote[]>;
    /** Optional so a store persisted before change requests existed still loads. */
    changes?: ChangeRequest[];
    /** 1 once the 4 sample change requests are in, so an older store gains them once. */
    changeSeed?: number;
    /** Schedules published in this preview (fixtures): version and when. Optional so an older store loads. */
    publishedTimelines?: Record<string, { version: number; at: string }>;
    seq: number;
}

/** The sample catalogue: the demo plan's lines, cut to the term. */
function catalogueLines(termMonths: number): Deliverable[] {
    return DEMO_ENGAGEMENT.deliverables
        .map((d) => ({ ...d, months: d.months.filter((m) => m <= termMonths) }))
        .filter((d) => d.months.length > 0);
}

/**
 * Fixtures only: who to tell when a schedule is published here, so the
 * sample business row (its `timeline_published_version`) says so too, as the
 * server's list does. The onboarding sample store listens.
 */
const publishedListeners = new Set<(businessId: string, version: number) => void>();
/**
 * Fixtures only: forget the sample plans of these businesses (their contract
 * and any published schedule), so a reset of the onboarding samples signs them
 * again from their own quote rather than an older preview's plan.
 */
export function forgetSampleEngagements(businessIds: string[]): void {
    if (isClientReviewLive) return;
    businessIds.forEach((b) => {
        delete store.engagements[b];
        if (store.publishedTimelines) delete store.publishedTimelines[b];
    });
    save();
}

export function onSampleTimelinePublished(cb: (businessId: string, version: number) => void): () => void {
    publishedListeners.add(cb);
    return () => publishedListeners.delete(cb);
}

/** The pages the catalogue lines deliver, as the server counts them: G1's monthly number times its months. */
function cataloguePages(lines: Deliverable[]): number {
    return lines
        .filter((d) => d.code.trim().toUpperCase() === "G1")
        .reduce((n, d) => n + (Number(/^\s*(\d+)/.exec(d.quantity ?? "")?.[1] ?? 0) * d.months.length), 0);
}

/**
 * The engagement, with the catalogue's lines written once when it was seeded
 * with none before the seed did so (a sample signed earlier in this browser):
 * the server always writes them, so no sample plan reads "Nothing lands".
 * A plan whose lines staff edited is left alone.
 */
function withCatalogue(b: string): Engagement | undefined {
    const e = store.engagements[b];
    if (!e) return e;
    // A sample plan an earlier preview wrote its catalogue lines into without the pages they give: the
    // server writes both together. Only while nobody has edited the lines or the pages since.
    const pages = cataloguePages(e.deliverables);
    if (e.pageUnits === 0 && pages > 0 && !e.changeLog.some((c) => /\b(line|page)/i.test(c.summary))) {
        store.engagements[b] = { ...e, pageUnits: pages };
        save();
        return store.engagements[b];
    }
    if (e.deliverables.length > 0 || e.pageUnits !== 0) return e;
    if (e.changeLog.some((c) => /\bline/i.test(c.summary))) return e;
    // The pages the contract sells are the pages those lines deliver, as the server writes them:
    // a plan reading "gives 12, the contract sells 0" was a seed that left them out (Angel, 15 Oct).
    const lines = catalogueLines(e.termMonths);
    store.engagements[b] = { ...e, deliverables: lines, pageUnits: cataloguePages(lines) };
    save();
    return store.engagements[b];
}

/**
 * The fixture's publish blockers, read from the contract as the server does
 * (BE `aeo_timeline.py`): fewer than 5 priority keywords recorded (#5256), and
 * a signature part missing (#5333). Saving them on the contract clears them.
 */
function keywordConflicts(e: Engagement): Array<{ code: string; message: string }> {
    const out: Array<{ code: string; message: string }> = [];
    const n = e.tracking?.priority_keywords?.length ?? 0;
    if (n < 5) {
        out.push({ code: "priority_keywords_incomplete", message: `priority_keywords has ${n} entries; the catalogue captures 5 to 10 at the quotation discussion. Get the rest before publishing.` });
    }
    const sig = e.tracking?.signature ?? {};
    const missing = [
        !sig.visibility ? "a question the business is already named for" : null,
        !sig.coverage ? "a question no rival is named for" : null,
    ].filter((m): m is string => !!m);
    if (missing.length > 0) {
        const it = missing.length === 1 ? "it" : "them";
        out.push({ code: "signature_incomplete", message: `The signature is missing ${missing.join(" and ")}. The AI check could not pick ${it}, so record ${it} on the plan, under What was sold.` });
    }
    return out;
}

/**
 * The fixture's first-publish diff, as the server's `diff_schedules(None, new)`:
 * every date in the schedule with `from` null, so the count is the truth.
 */
function firstPublishDiff(s: Schedule): DateMove[] {
    const term: Array<[string, string, string | null | undefined]> = [
        ["clock_start", "Work starts", s.clock_start],
        ["handover", "Handover", s.handover],
        ["term_end", "Term ends", s.term_end],
        ["keywords_by", "Keywords needed by", s.keywords_by],
    ];
    const out: DateMove[] = term.filter(([, , to]) => !!to).map(([field, label, to]) => ({ scope: "term", termMonth: null, field, label, from: null, to: to ?? null }));
    s.months.forEach((m) => {
        ([["start", "Month starts", m.start], ["end", "Month ends", m.end], ["report_date", "Report", m.report_date]] as const).forEach(([field, label, to]) => {
            if (to) out.push({ scope: "month", termMonth: m.term_month, field, label, from: null, to });
        });
    });
    return out;
}

/**
 * Nothing in the schedule lands in any month: no plan lines to date. Live,
 * the server writes the lines from its catalogue and lands the AI check every
 * month, so this is a seed with no lines. Never publishable: the client would
 * be told a schedule with nothing in it.
 */
export function scheduleIsEmpty(p: Pick<TimelinePayload, "computed">): boolean {
    return !p.computed || p.computed.months.length === 0 || p.computed.months.every((m) => m.lands.length === 0);
}

/**
 * What stands between this schedule and a publish, in staff words, for the
 * onboarding Next card: the same conflicts the Timeline lists, in the same order.
 */
export function publishBlockers(p: Pick<TimelinePayload, "computed" | "conflicts">): string[] {
    const out: string[] = [];
    for (const c of p.conflicts) {
        if (c.code === "priority_keywords_incomplete") {
            const n = p.computed?.priority_keywords?.length ?? Number(/has (\d+) entr/.exec(c.message)?.[1] ?? 0);
            out.push(`${n} of the 5 to 10 priority keywords recorded`);
        } else if (c.code === "signature_incomplete") out.push("the signature questions not recorded");
        else if (c.code === "outlets_missing") out.push("the outlet names not recorded");
        else out.push("1 more item on the timeline");
    }
    if (scheduleIsEmpty(p)) out.push("no plan lines, so nothing is scheduled");
    return out;
}

/** What the publish button, the onboarding Next card and the Overview read alike: the server's yes, and something to send. */
export function timelinePublishable(p: Pick<TimelinePayload, "computed" | "canPublish">): boolean {
    return p.canPublish && !scheduleIsEmpty(p);
}

/**
 * Versioned so a preview browser reseeds when the demo changes shape. The
 * store persists in localStorage and only seeds when empty, so without a new
 * key an old demo keeps its old items and the new fields never appear.
 * v3: the account manager is Angel on every seeded pack, thread and note.
 * v4: the sample contract sells 12 pages (G1's), as a real quotation does; month 1's 10 are B1's own.
 */
const KEY = "__oura_client_review_store_v4__";
/** The signed-in merchant in the demo. The real one comes from the JWT. */
export const DEMO_CLIENT = { businessId: "nyonya-table", name: "Siew Ling" };
/** The account manager in the demo: Angel manages every client and signs every weekly note. */
export const DEMO_STAFF = { name: "Angel" };
/** The address a client reaches their account manager at. Demo value for the optional `contactEmail`. */
const DEMO_CONTACT_EMAIL = "oura@ourteam.ai";

function load(): Store {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (raw) return JSON.parse(raw) as Store;
    } catch { /* private mode, or a stale shape: start clean */ }
    return { engagements: {}, approvals: {}, packs: [], threads: [], notifications: [], calls: [], seq: 0 };
}
let store: Store = load();
if (!store.engagements) store.engagements = {};

/**
 * Demo content so the merchant screens have something to show on first open,
 * for the sample business only. Runs once, only when the store is empty.
 * DELETE with the rest of the mock.
 *
 * Three packs, so every answer the merchant can give is reachable: a free-text
 * ask that is past its reply-by date, a proposal to confirm, a choice with
 * options, a file, and answered and approved ones with a thread behind them.
 * Pack blocks cover a section, a table and an evidence placeholder.
 *
 * Dates are fixed rather than relative to today, so "late" and "coming up"
 * read the way the design shows them (the term started 18 Aug 2026).
 */
function seedDemo() {
    const b = DEMO_CLIENT.businessId;
    if (!store.engagements[b]) store.engagements[b] = { ...DEMO_ENGAGEMENT, changeLog: [{ at: "2026-08-11T03:00:00Z", by: DEMO_STAFF.name, summary: "Seeded from quotation OT-2026-NYO-001" }] };
    if (store.packs.length > 0) return;
    const client = DEMO_CLIENT.name, staff = DEMO_STAFF.name;
    const pending = { decision: "pending" as const, decidedAt: null, decidedBy: null, answer: null, fileName: null, waitingOn: "client" as const };
    // Every item below carries `replyBy` and `holdsUp`, and the free-text ones a `placeholder`:
    // fixture-only until ourteam-backend handoff: Reply-by date and 'holds up' on merchant asks
    // fixture-only until ourteam-backend handoff: Answer hint (placeholder) per ask

    const details: PackItem[] = [
        { id: "item_demo_details", packId: "pack_demo_details", taskId: "demo_t4", kind: "choose", title: "Business details, agreed in writing", why: "Your address, phone and 3 signature items, in your words. Every page we write uses them, so they have to match what customers see everywhere else.", tags: [], version: 1, decision: "answered", decidedAt: "2026-09-10T03:20:00Z", decidedBy: client, answer: "Address and phone as on the website. Our 3 signatures: ayam buah keluak, nyonya laksa and chap chye.", fileName: null, waitingOn: null, replyBy: "2026-09-10", holdsUp: "every new page", placeholder: "Your address, phone and 3 signature items" },
        { id: "item_demo_3", packId: "pack_demo_details", taskId: "demo_t3", kind: "approve", title: "A page that answers \"private dining in Bukit Bintang\"", why: "3 of your 50 questions ask this and no page on your site answers it. This is one of your 2 signature pages.", tags: ["New page", "Signature page"], outline: ["What the private room seats, and the minimum spend", "Set prices for groups", "How to book it, with your phone number on the page"], version: 1, decision: "approved", decidedAt: "2026-09-09T06:00:00Z", decidedBy: client, answer: null, fileName: null, waitingOn: null, replyBy: "2026-09-11", holdsUp: "your signature page" },
    ];
    const month: PackItem[] = [
        { id: "item_demo_1", packId: "pack_demo", taskId: "demo_t1", kind: "approve", title: "New opening paragraph for your homepage", why: "AI answers quote the first paragraph they find. Yours says who you are but not where you are or what you offer. The new one answers both in 2 sentences.", tags: ["Copy change"], current: "Welcome to Nyonya Table. We are passionate about authentic Peranakan cuisine.", proposed: "Nyonya Table is a Peranakan restaurant in Bukit Bintang, open Tuesday to Sunday for lunch and dinner. Set lunches, group bookings and a private room for 12.", version: 2, decision: "approved", decidedAt: "2026-09-21T03:00:00Z", decidedBy: client, answer: null, fileName: null, waitingOn: null, replyBy: "2026-09-25", holdsUp: "your homepage" },
        { id: "item_demo_prices", packId: "pack_demo", taskId: "demo_t5", kind: "choose", title: "Send your prices", why: "4 pages need a price. AI answers quote the business that shows one. A list or a photo works.", tags: [], version: 1, ...pending, replyBy: "2026-09-22", holdsUp: "4 pages", placeholder: "Type the prices, or add a photo of your price list." },
        { id: "item_demo_2", packId: "pack_demo", taskId: "demo_t2", kind: "choose", title: "Which phone number should AI answers give?", why: "Your website lists 2 numbers and AI answers pick either. Choose the one customers should call and we use it everywhere.", tags: [], options: ["6468 1234, the restaurant", "9123 4567, WhatsApp bookings", "Both, the restaurant first"], version: 1, ...pending, replyBy: "2026-10-02", holdsUp: "your contact page" },
        { id: "item_demo_logo", packId: "pack_demo", taskId: "demo_t6", kind: "provide_file", title: "Your logo as a file", why: "The logo on your site is a small, blurry copy. A clear file makes the new pages look like yours.", tags: [], version: 1, ...pending, replyBy: "2026-10-02", holdsUp: "the new pages" },
    ];
    const hours: PackItem[] = [
        { id: "item_demo_hours", packId: "pack_demo_hours", taskId: "demo_t7", kind: "approve", title: "Confirm your opening hours", why: "Your website shows no opening hours, so AI answers guess them or leave you out. This is what we will publish.", tags: ["Copy change"], proposed: "Tue to Sun, 11:30 to 21:30. Closed Monday.", version: 1, ...pending, replyBy: "2026-09-28", holdsUp: "your contact page" },
    ];
    store.packs.push(
        {
            id: "pack_demo_hours", businessId: b, ref: "OT-2026-NYO-003", title: "Your opening hours",
            intro: "1 thing to confirm. It goes on your contact page as soon as you say yes.",
            status: "published", preparedBy: staff, createdAt: "2026-09-23T01:30:00Z", publishedAt: "2026-09-23T02:00:00Z", submittedAt: null,
            blocks: [{ id: "bh1", type: "decision", itemId: "item_demo_hours" }],
            items: hours,
        },
        {
            id: "pack_demo", businessId: b, ref: "OT-2026-NYO-002", title: "What your new pages need",
            intro: "3 answers and 1 approval. The new pages go live once we have them.",
            status: "published", preparedBy: staff, createdAt: "2026-09-17T09:00:00Z", publishedAt: "2026-09-18T02:00:00Z", submittedAt: null,
            blocks: [
                { id: "bd1", type: "section", heading: "Why we are asking", body: "Each of these is something your customers will see, or a fact only you know. Nothing goes live until you say yes." },
                { id: "bd2", type: "decision", itemId: "item_demo_1" },
                { id: "bd3", type: "table", title: "The pages that need a price", columns: ["Page", "Price shown now"], rows: [["Set lunch", "None"], ["Private dining", "None"], ["Catering", "None"], ["Gift vouchers", "None"]] },
                { id: "bd4", type: "decision", itemId: "item_demo_prices" },
                { id: "bd5", type: "evidence", caption: "Where the price goes", label: "The set lunch page today, with no price" },
                { id: "bd6", type: "decision", itemId: "item_demo_2" },
                { id: "bd7", type: "decision", itemId: "item_demo_logo" },
                { id: "bd8", type: "section", heading: "What happens next", body: "You answer here, or ask a question under any item. We start the same day and record every change on your Home page." },
            ],
            items: month,
        },
        {
            id: "pack_demo_details", businessId: b, ref: "OT-2026-NYO-001", title: "Your business details",
            intro: "1 answer and 1 page to approve.",
            status: "submitted", preparedBy: staff, createdAt: "2026-09-07T09:00:00Z", publishedAt: "2026-09-08T02:00:00Z", submittedAt: "2026-09-10T03:20:00Z",
            blocks: [
                { id: "bb1", type: "decision", itemId: "item_demo_details" },
                { id: "bb2", type: "decision", itemId: "item_demo_3" },
            ],
            items: details,
        },
    );
    const message = (id: string, authorKind: ThreadMessage["authorKind"], authorName: string, body: string, at: string, kind: MessageKind = "message"): ThreadMessage =>
        ({ id, kind, authorKind, authorName, body, fileName: null, suggestedEdit: null, at });
    store.threads.push(
        { id: "t_demo_1", itemId: "item_demo_1", businessId: b, status: "resolved", messages: [
            message("m_demo_1", "client", client, "Can we mention the private room seats 12? People ask that on the phone every week.", "2026-09-19T04:00:00Z"),
            message("m_demo_2", "staff", staff, "Yes. Added it to the wording above. Have another look when you can.", "2026-09-19T08:00:00Z"),
            message("m_demo_3", "staff", staff, `${staff} updated the wording. Version 1 to version 2.`, "2026-09-19T08:00:00Z", "version"),
            message("m_demo_4", "client", client, `${client} approved this.`, "2026-09-21T03:00:00Z", "decision"),
        ] },
        { id: "t_demo_prices", itemId: "item_demo_prices", businessId: b, status: "open", messages: [
            message("m_demo_5", "staff", staff, "A photo of your price list is all I need. I will type it up.", "2026-09-25T06:00:00Z"),
        ] },
        { id: "t_demo_2", itemId: "item_demo_2", businessId: b, status: "open", messages: [] },
        { id: "t_demo_logo", itemId: "item_demo_logo", businessId: b, status: "open", messages: [] },
        { id: "t_demo_hours", itemId: "item_demo_hours", businessId: b, status: "open", messages: [] },
        { id: "t_demo_details", itemId: "item_demo_details", businessId: b, status: "resolved", messages: [
            message("m_demo_6", "client", client, `${client} answered.`, "2026-09-10T03:20:00Z", "decision"),
        ] },
        { id: "t_demo_3", itemId: "item_demo_3", businessId: b, status: "resolved", messages: [
            message("m_demo_7", "client", client, `${client} approved this.`, "2026-09-09T06:00:00Z", "decision"),
        ] },
    );
    store.notifications.push(
        { id: "n_demo_1", audience: "client", businessId: b, title: `${staff} replied`, body: "On \"Send your prices\": A photo of your price list is all I need.", itemId: "item_demo_prices", packId: "pack_demo", read: false, at: "2026-09-25T06:00:00Z" },
        { id: "n_demo_2", audience: "client", businessId: b, title: "1 decision waiting for you", body: `${staff} sent "Your opening hours".`, itemId: null, packId: "pack_demo_hours", read: false, at: "2026-09-23T02:00:00Z" },
        { id: "n_demo_3", audience: "client", businessId: b, title: "4 decisions waiting for you", body: `${staff} sent "What your new pages need".`, itemId: null, packId: "pack_demo", read: true, at: "2026-09-18T02:00:00Z" },
        { id: "n_demo_4", audience: "staff", businessId: b, title: `${client} approved this`, body: "New opening paragraph for your homepage", itemId: "item_demo_1", packId: "pack_demo", read: false, at: "2026-09-21T03:00:00Z" },
    );
    // The last 3 weekly notes, newest first, as `listClientNotes` returns them.
    // The merchant's `weekNote` is read from the newest, the way the server
    // reads `OuraClientNote`.
    store.notes = { ...store.notes, [b]: [
        { id: "note_demo_3", date: "2026-09-25T09:00:00Z", author: staff, body: "2 of your 8 pages are approved and in writing. The signature page comes to you on Fri 2 Oct. For the prices, a photo of your price list is all I need." },
        { id: "note_demo_2", date: "2026-09-18T09:00:00Z", author: staff, body: "Images on 10 pages are compressed. Your homepage now loads 4 times faster." },
        { id: "note_demo_1", date: "2026-09-11T09:00:00Z", author: staff, body: "Your site is backed up and we have admin access. We fixed 3 broken links today." },
    ] };
    store.seq = Math.max(store.seq, 100);
    save();
}

function save() { try { window.localStorage.setItem(KEY, JSON.stringify(store)); } catch { /* ignore */ } }
/** Dev helper: wipe the demo. Exposed on window so it can be run from the console. */
export function resetClientReviewStore() { store = { engagements: {}, approvals: {}, packs: [], threads: [], notifications: [], calls: [], changes: [], seq: 0 }; save(); }
if (typeof window !== "undefined") (window as unknown as { __ouraResetClientReview?: () => void }).__ouraResetClientReview = resetClientReviewStore;

const nowIso = () => new Date().toISOString();
const nextId = (p: string) => `${p}_${++store.seq}`;
function delay<T>(v: T, ms = 160): Promise<T> {
    save();
    return new Promise((r) => window.setTimeout(() => r(JSON.parse(JSON.stringify(v)) as T), ms));
}
function notify(audience: Audience, businessId: string, title: string, body: string, itemId: string | null = null, packId: string | null = null, link: string | null = null) {
    store.notifications.unshift({ id: nextId("n"), audience, businessId, title, body, itemId, packId, read: false, at: nowIso(), link });
}

/* Change requests in the sample store: the docs dev mode applies ("slow" waits 3 s, "error" fails every call). */

/** The client's email in the demo, where Angel's reply goes. */
const DEMO_CLIENT_EMAIL = "siewling@nyonyatable.com";

/** Files the mock `uploadFile` handed out this session, so a request can show and open them. */
const mockUploads = new Map<string, { name: string; url: string }>();

/** Every stored request in the contract shape, including ones saved before the shape grew. */
function changesOf(): ChangeRequest[] {
    store.changes = (store.changes ?? []).map((c) => normaliseChangeRequest(c));
    return store.changes;
}

/** A seeded file has no upload behind it: it gets a small demo PDF, made once per session. */
function withFileUrls(c: ChangeRequest): ChangeRequest {
    return {
        ...c,
        files: c.files.map((f) => {
            const live = mockUploads.get(f.fileKey)?.url;
            if (live) return { ...f, url: live };
            if (f.url && !f.url.startsWith("blob:")) return f;
            return { ...f, url: demoPdfUrl({ id: f.fileKey, title: f.name.replace(/\.pdf$/i, ""), date: c.at, url: null, summary: "The file the client attached to their request." }) };
        }),
    };
}

/** The current Malaysian calendar month, "2026-10": the month a small change counts in. */
const myMonth = () => malaysianMonthNow();

/** Runs a change-request call the way the fake server answers: the dev mode first, then the work, then the latency. */
function changeCall<T>(work: () => T): Promise<T> {
    if (docsDevMode() === "error") {
        return new Promise((_resolve, reject) => window.setTimeout(() => reject(new ChangeRequestError("Could not reach the server.")), docsMockDelayMs()));
    }
    try {
        return delay(work(), docsMockDelayMs());
    } catch (err) {
        return Promise.reject(err);
    }
}

function changeRefusal(code: DocsErrorCode, status: number, existingId: string | null = null): ChangeRequestError {
    return new ChangeRequestError(DOCS_ERROR_MESSAGE[code], { code, status, existingId });
}

/**
 * The 4 sample requests on the demo business: 1 open with a PDF, 1 answered,
 * 1 filed as this month's small change, 1 done. Once per store.
 */
function seedChangeRequests() {
    if (store.changeSeed === 2) return;
    const b = DEMO_CLIENT.businessId, client = DEMO_CLIENT.name, staff = DEMO_STAFF.name;
    const ago = (hours: number) => new Date(Date.now() - hours * 3_600_000).toISOString();
    const e = store.engagements[b];
    const base = { businessId: b, requestedBy: client, requesterEmail: DEMO_CLIENT_EMAIL, files: [], reply: null, smallChange: null, doneAt: null, doneBy: null };
    const seeded: ChangeRequest[] = [
        {
            ...base, id: "change_demo_open", status: "open", at: ago(3),
            note: "Our new Sunday brunch set is ready. Can it go on the menu page? The PDF has the dishes and prices.",
            files: [{ fileKey: `oura/uploads/${b}/sunday-brunch-set.pdf`, name: "Sunday brunch set.pdf", url: "" }],
            // As the reader files a pasted message: a title, the brief, and their words kept whole.
            source: "text",
            title: "Sunday brunch set on the menu page, with dishes and prices from their PDF",
            where: "Website",
            why: "The brunch set starts this Sunday and guests search for brunch menus before they book.",
            checklist: ["Add the 6 brunch dishes with prices", "Add the RM58 set price and what it includes", "Say it runs Sundays, 10am to 3pm"],
            notes: ["Key message: a relaxed Peranakan brunch, only on Sundays", "Use the dish names exactly as the PDF spells them"],
            original: {
                text: "Hi team! Our new Sunday brunch set is ready. Can it go on the menu page? The PDF has the dishes and prices. It's RM58 a person and runs 10am to 3pm, Sundays only. Please keep the dish names exactly as in the PDF. Thanks!",
                fileKeys: [`oura/uploads/${b}/sunday-brunch-set.pdf`],
                email: null,
            },
        },
        {
            ...base, id: "change_demo_g5", status: "open", at: ago(26),
            note: "Please change the set lunch price on the menu page from RM38 to RM42.",
            smallChange: { month: myMonth(), termMonth: e ? monthOf(e) : null, filedBy: staff, filedAt: ago(1) },
        },
        {
            ...base, id: "change_demo_answered", status: "answered", at: ago(72),
            note: "Can you add our new Sunday hours, 10am to 4pm, to the contact page?",
            reply: { body: "Yes. The contact page shows the Sunday hours from tomorrow morning.", by: staff, at: ago(50) },
        },
        {
            ...base, id: "change_demo_done", status: "done", at: ago(300),
            note: "The private dining page says \"minimun spend\". Please fix it to \"minimum spend\".",
            reply: { body: "Fixed, thank you for spotting it.", by: staff, at: ago(290) },
            doneAt: ago(290), doneBy: staff,
        },
    ];
    store.changes = [...changesOf().filter((c) => !seeded.some((x) => x.id === c.id)), ...seeded];
    store.changeSeed = 2;
    save();
}
function findItem(itemId: string): { pack: Pack; item: PackItem } | null {
    for (const pack of store.packs) {
        const item = pack.items.find((i) => i.id === itemId);
        if (item) return { pack, item };
    }
    return null;
}
function threadFor(item: PackItem, businessId: string): Thread {
    let t = store.threads.find((x) => x.itemId === item.id);
    if (!t) {
        t = { id: nextId("t"), itemId: item.id, businessId, status: "open", messages: [] };
        store.threads.push(t);
    }
    return t;
}
function refFor(businessId: string): string {
    const n = store.packs.filter((p) => p.businessId === businessId).length + 1;
    const slug = businessId.replace(/[^a-z]/gi, "").slice(0, 3).toUpperCase() || "CLI";
    return `OT-2026-${slug}-${String(n).padStart(3, "0")}`;
}

/**
 * `d` plus `k` months, CLAMPED to the end of the target month.
 *
 * 31 January plus one month is 28 February (29 in a leap year), not 2 or 3
 * March. `Date.setMonth` overflows instead of clamping, which is why this is
 * written out rather than being a one-liner. It mirrors the backend's
 * `add_months` (`app/dependencies/aeo_timeline.py`), and the schedule's windows
 * are drawn with that, so the two have to round the same way or they describe
 * different dates for every contract that starts on a 29th, 30th or 31st.
 */
export function addMonths(d: Date, k: number): Date {
    const y = d.getFullYear(), m = d.getMonth() + k, day = d.getDate();
    const last = new Date(y, m + 1, 0).getDate();   // last day of the target month
    return new Date(y, m, Math.min(day, last));
}

/** Whole months from `start` to `today` under the anniversary rule. */
function monthsElapsed(start: Date, today: Date): number {
    let k = (today.getFullYear() - start.getFullYear()) * 12
        + (today.getMonth() - start.getMonth());
    while (addMonths(start, k) > today) k -= 1;
    while (addMonths(start, k + 1) <= today) k += 1;
    return k;
}

/**
 * Which month of the term it is. THE ONE frontend definition (#3748 / backend
 * #4820) — `PlanEditor` calls this rather than computing its own.
 *
 * A TERM IS MEASURED BY ANNIVERSARIES OF ITS START DATE, never by calendar
 * months. A contract that starts on 18 August runs the 18th to the 17th, so it
 * is still month 1 on 2 September and enters month 2 on the 18th. The old
 * calendar subtraction rolled over on the 1st and told a merchant they were a
 * month further through a term they had bought through the 17th.
 *
 * DEFINED AS "the largest k where addMonths(start, k) <= today", NOT by
 * comparing day numbers. A term starting on the 31st has no 31 February: the
 * schedule clamps that anniversary to the 28th while a day-number comparison
 * holds out for a 31st that never arrives, and the two then disagree for the
 * whole of February and March. Sharing `addMonths` makes the counter and the
 * schedule agree by construction rather than by luck.
 *
 * Month 1 is Foundation; after that Growth. A null start is month 1 — the plan
 * has not begun, and the merchant reads "Starts when your payment clears".
 */
export function monthOf(e: Pick<Engagement, "startedAt" | "termMonths">): number {
    if (!e.startedAt) return 1;
    const s = new Date(e.startedAt);
    if (Number.isNaN(s.getTime())) return 1;
    return Math.max(1, Math.min(e.termMonths, monthsElapsed(s, new Date()) + 1));
}
/**
 * The demo site audit, keyed the way `measured{}` is. A key that is ABSENT here
 * has never been measured for this business and reads `null` — `no_schema` is
 * left out on purpose so the not-yet-measured path is reachable in the mock.
 */
const DEMO_CHECKS: Record<string, MeasuredCheck> = {
    broken_4xx: { label: "Broken pages (4xx)", severity: "error", count: 0, state: "clear" },
    broken_outbound: { label: "Dead outbound links", severity: "warning", count: 2, state: "failing" },
    redirect_chains: { label: "Redirect chains", severity: "warning", count: 0, state: "clear" },
    missing_meta: { label: "Missing meta descriptions", severity: "warning", count: 3, state: "failing" },
    dup_titles: { label: "Duplicate titles", severity: "warning", count: 0, state: "clear" },
    missing_alt: { label: "Images without alt text", severity: "warning", count: 6, state: "failing" },
    orphan_pages: { label: "Orphan pages", severity: "warning", count: 0, state: "clear" },
    cwv: { label: "Core Web Vitals", severity: "error", count: 4, state: "failing" },
    robots_missing: { label: "robots.txt", severity: "error", count: 0, state: "clear" },
    canonicals: { label: "Canonical tags", severity: "warning", count: 1, state: "failing" },
    thin_content: { label: "Thin pages", severity: "warning", count: 5, state: "failing" },
};

/** What the server computes on read: one entry per covered key, `null` for a key no assessment carries. */
function measuredFor(covers: string[]): Record<string, MeasuredCheck | null> {
    return Object.fromEntries(covers.map((k) => [k, DEMO_CHECKS[k] ?? null]));
}

function planFromEngagement(e: Engagement, month: number, statusOf: (code: string) => ClientPlanStatus): ClientPagePayload["plan"] {
    const items: ClientPlanItem[] = e.deliverables.filter((d) => d.months.includes(month)).map((d) => {
        const covers = d.covers ?? [];
        const reportedByHand = d.reported_by_hand ?? covers.length === 0;
        // Reported by hand is NOT not-started: a line that covers no measured
        // check is one we account for manually, which is a legitimate state.
        const derived = statusOf(d.code);
        const status: ClientPlanStatus = derived === "not_started" && reportedByHand ? "reported_by_hand" : derived;
        const note = status === "waiting_on_you" ? "Waiting on you" : d.quantity ?? d.note;
        return {
            code: d.code, label: d.label, status, note,
            noteTone: status === "waiting_on_you" ? "warn" : "muted",
            covers, reportedByHand, measured: measuredFor(covers),
        };
    });
    return { phase: month === 1 ? "foundation" : "growth", month, items };
}

/** Delivery's deliverables as the client's plan lines: the state word decides the status. */
const WORD_STATUS: Record<DeliveryDeliverable["clientWord"], ClientPlanStatus> = {
    Done: "done",
    "In progress": "in_progress",
    "Needs you": "waiting_on_you",
    "Not started": "not_started",
};

function deliveryItems(e: Engagement, ds: DeliveryDeliverable[], month: number): ClientPlanItem[] {
    const shown = ds.filter((d) => d.month === month || (d.month < month && d.group !== "done"));
    return shown.map((d) => {
        const line = e.deliverables.find((x) => x.code === d.code);
        const covers = line?.covers ?? [];
        const status = WORD_STATUS[d.clientWord];
        // Only what was fixed: a live page, or a check's pages fixed or closed by hand.
        // A check that never failed, and pages staff set to No action, fixed nothing.
        const fixed = d.items
            .filter((i) => i.state === "done" && !i.doesNotApply && (i.kind === "check" || i.kind === "page"))
            .reduce<string[]>((all, i) => {
                if (i.kind === "page") return all.concat(`${i.label}, live`);
                const n = i.pages.filter((p) => p.state === "done" || p.state === "closed_by_hand").length;
                return n > 0 ? all.concat(`${i.label} on ${n === 1 ? "1 page" : `${n} pages`}`) : all;
            }, []);
        return {
            code: d.code,
            key: d.key,
            label: d.label,
            status,
            note: status === "waiting_on_you" ? "Waiting on you" : d.month < month ? `From month ${d.month}` : line?.quantity ?? line?.note ?? null,
            noteTone: status === "waiting_on_you" ? "warn" : "muted",
            covers,
            reportedByHand: false,
            // The same items staff see: a covered check's open pages, or clear.
            measured: Object.fromEntries(
                covers.map((k) => {
                    const it = d.items.find((i) => i.checkKey === k);
                    const base = DEMO_CHECKS[k];
                    if (!it) return [k, base ?? null];
                    const open = it.pages.filter((p) => p.state === "todo" || p.state === "waiting" || p.state === "not_read").length;
                    return [k, { label: base?.label ?? it.label, severity: base?.severity ?? null, count: open, state: open > 0 ? "failing" : "clear" } as MeasuredCheck];
                }),
            ),
            line: d.clientLine,
            fixed,
            month: d.month < month ? d.month : null,
        };
    });
}

/**
 * Whole calendar days an ask is past its reply-by date, as the server will compute it:
 * 0 once it is answered or while it is not yet due. The mock stands in for the
 * server here, so this is the one place a lateness is worked out from today.
 */
function lateDaysFor(item: PackItem): number {
    if (!item.replyBy || item.decision !== "pending") return 0;
    const due = new Date(`${item.replyBy}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((today.getTime() - due.getTime()) / 86_400_000));
}

/** A pack as the read side returns it: lateness only on items that carry a reply-by date. */
function withLateness(pack: Pack): Pack {
    return { ...pack, items: pack.items.map((i) => (i.replyBy ? { ...i, lateDays: lateDaysFor(i) } : i)) };
}

/** The sample merchant's page, as `build_client_page` assembles it. `viewer` is whoever is reading. */
function demoClientPage(viewer: string): ClientPagePayload {
    const b = DEMO_CLIENT.businessId;
    const packs = store.packs.filter((p) => p.businessId === b && p.status !== "draft").map(withLateness);
    const decisions: ClientDecisionRow[] = packs.flatMap((p) => p.items.filter((i) => i.decision !== "pending").map((i) => ({
        id: i.id, title: i.title, decision: i.decision, decidedAt: i.decidedAt,
        workStatus: i.decision === "approved" || i.decision === "answered" ? "In progress" : "Not started",
    })));
    // A business with no engagement gets nulls, not a borrowed one (#3711
    // AC3). This is what makes the empty merchant page reachable at all.
    const e = store.engagements[b] ?? null;
    let month = e ? monthOf(e) : 0;
    try { if (e && window.localStorage.getItem("__oura_demo_phase") === "growth") month = Math.max(month, 3); } catch { /* ignore */ }
    // The server joins only asks still waiting, so an answered one drops off its line.
    const waitingIds = new Set(packs.flatMap((p) => p.items).filter((i) => i.decision === "pending").map((i) => i.id));
    // G5 as B9 builds it: the small change filed for this month decides the line (done once every
    // one filed is done); with none filed, its ask decides it, and an answered ask no longer waits on you.
    const smallChanges = changesOf().filter((c) => c.businessId === b && c.smallChange?.month === myMonth());
    const g5Asks = (DEMO_PLAN_EXTRAS.G5?.itemIds ?? []).filter((id) => waitingIds.has(id));
    const g5Status = (): ClientPlanStatus =>
        smallChanges.length > 0
            ? smallChanges.every((c) => c.status === "done") ? "done" : "in_progress"
            : g5Asks.length > 0 ? "waiting_on_you" : "in_progress";
    // Only a code the tracker has something for gets a status (#3711 AC4).
    // DEMO_STATUS used to answer for every code, so every line rendered as
    // done, in progress or waiting on you whatever the tracker said.
    const statusOf = (code: string): ClientPlanStatus =>
        store.engagements[b] ? (code === "G5" ? g5Status() : DEMO_STATUS[code] ?? (month > 1 ? "in_progress" : "not_started")) : "not_started";
    const planBase = e ? planFromEngagement(e, month, statusOf) : null;
    // Delivery's status on the client's page (H6): this month's deliverables plus
    // earlier ones still open, each with its client line, the same "N of M" as staff read.
    const delivered = e ? sampleDeliverables(b, e) : null;
    const plan = planBase && delivered ? { ...planBase, items: deliveryItems(e!, delivered, month) } : planBase;
    const slotKind = new Map<string, "new" | "improved">();
    (delivered ?? []).filter((d) => d.code === "B1" || d.code === "G1").forEach((d) => d.items.forEach((i) => {
        if (i.slot?.pageKind && i.slot.topic) slotKind.set(i.slot.topic, i.slot.pageKind === "new" ? "new" : "improved");
    }));
    const note = store.notes?.[b]?.[0] ?? null;
    return {
        ...DEMO_PAGE,
        engagement: e,
        plan: plan && {
            ...plan,
            items: plan.items.map((it) => {
                // This month's small change, filed by staff from a request: the line names it and waits on no ask.
                if (it.code === "G5" && smallChanges.length > 0) {
                    const note = smallChanges[0].note.replace(/\s+/g, " ").trim();
                    const said = note.length > 140 ? `${note.slice(0, 139).trimEnd()}…` : note;
                    return { ...it, itemIds: [], description: `Under 30 minutes of work. This month: “${said}”` };
                }
                const extra = DEMO_PLAN_EXTRAS[it.code];
                return extra ? { ...it, description: extra.description, itemIds: extra.itemIds.filter((id) => waitingIds.has(id)) } : it;
            }),
        },
        subscription: e ? { ref: e.ref, name: e.planName, startedAt: e.startedAt, termMonths: e.termMonths, pagesPublished: month > 1 ? 10 : 4, pageUnits: e.pageUnits, outlets: e.outlets } : null,
        pages: delivered && DEMO_PAGE.pages ? { ...DEMO_PAGE.pages, published: DEMO_PAGE.pages.published.map((p) => ({ ...p, kind: slotKind.get(p.title) })) } : DEMO_PAGE.pages,
        // A document that was never uploaded has no link, and must not be handed a demo one.
        // The docs sample's filed approved PDFs (B24) are report rows of kind doc_approved, as the server adds them.
        reports: [...(isDocsSample ? mockApprovedReportLinks(b) : []), ...DEMO_PAGE.reports.map((r) => ({ ...r, url: r.url ?? (r.urlAbsent ? null : demoPdfUrl(r)) }))],
        business: { id: b, name: "Nyonya Table", city: "Bukit Bintang" },
        viewer: { name: viewer },
        packs,
        threads: store.threads.filter((t) => t.businessId === b),
        decisions: [...decisions, ...DEMO_PAGE.decisions],
        // fixture-only until ourteam-backend handoff: Friday note author on the merchant payload
        weekNote: note ? { date: note.date, body: note.body, author: note.author } : null,
        // The docs sample store's view of this client: only what is shared with them.
        documents: isDocsSample ? mockMerchantDocuments(b) : undefined,
    };
}

const mock = {
    // Null for a business nobody seeded (#3711 AC1). Falling back to the demo
    // meant every business in the mock looked sold, so the absent path — the
    // one P2-2 just built — could never be reached to test it.
    //
    // It also makes the activation below demonstrable end to end: a fresh
    // business has no engagement, so the first Start a plan succeeds and the
    // second answers 409, exactly as the server does.
    getEngagement: (b: string) => delay(withCatalogue(b) ?? null),
    /**
     * The fixture activation. Deliberately exercises the refusals, because the
     * happy path is the half least likely to rot: a business that already has
     * an engagement answers 409 the way the server does, so the screen's
     * "re-running the flow is refused" path is reachable without a database.
     */
    activateBusiness(b: string, input: ActivationInput): Promise<ActivationResult | ActivationFailure> {
        if (store.engagements[b]) {
            return delay({
                code: "already_activated",
                message: "This business already has an engagement. Activation does not replace one; edit the plan or re-seed it deliberately.",
            });
        }
        const email = input.contact.email.trim().toLowerCase();
        if (!email.includes("@") || !input.contact.name.trim()) {
            return delay({ code: "invalid_request", message: "contact.email and contact.name are required" });
        }
        if (email.includes("taken")) {
            return delay({ code: "email_in_another_workspace", message: "That email already belongs to another workspace" });
        }
        // What the server does with a seed: it authors the missing fields and
        // stores a complete row. The mock stands in for that authoring rather
        // than pretending a seed and a stored engagement are the same shape.
        store.engagements[b] = {
            ...input.engagement,
            // Authoring the two fields #3745 made required on a STORED row: a
            // seed may leave `covers` absent ("author it for me"), and only the
            // server decides what that becomes. Written out here because the
            // combination of #3712 and #3745 is what makes it necessary —
            // neither branch needed it alone.
            deliverables: input.engagement.deliverables.map((d) => ({
                ...d,
                covers: d.covers ?? [],
                reported_by_hand: d.reported_by_hand ?? (d.covers ?? []).length === 0,
            })),
            changeLog: [{ at: nowIso(), by: DEMO_STAFF.name, summary: `Seeded from quotation ${input.engagement.ref}` }],
        };
        const subdomain = b.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
        return delay({
            business: { id: b, domain: `${subdomain}.example`, businessName: input.engagement.client, companyId: `company_${subdomain}` },
            engagement: store.engagements[b],
            merchant: { email, userId: `user_${subdomain}`, subdomain, workspaceUrl: `https://${subdomain}.staging.ourteam.ai` },
        }, 700);
    },
    /**
     * A fixture timeline built from the demo engagement, so the Timeline tab is
     * reviewable before the endpoint is reachable locally. Carries a conflict on
     * purpose: "publishing is blocked while any remain" is the half of the
     * screen that never gets exercised if the fixture is always clean.
     */
    async publishTimeline(b: string): Promise<PublishResult | TimelineFailure> {
        // The fixture timeline carries a conflict on purpose, so the refusal is
        // what a reviewer meets first — it is the half of the screen that never
        // gets exercised when the fixture is always clean.
        const e = store.engagements[b] ?? DEMO_ENGAGEMENT;
        const conflicts = keywordConflicts(e);
        if (conflicts.length > 0) {
            return delay({
                code: "timeline_has_conflicts",
                message: `The schedule has ${conflicts.length} unresolved conflict${conflicts.length === 1 ? "" : "s"}. Resolve them before publishing: a published version is what the merchant is told, and a conflict is the contract and the catalogue disagreeing.`,
            });
        }
        // The demo client's version the page was told stays; any other client gets its first version here.
        const told = b === DEMO_CLIENT.businessId ? DEMO_PAGE.schedule ?? null : null;
        const was = store.publishedTimelines?.[b];
        if (told || was) return delay<PublishResult>({ published: false, datesMoved: 0, version: was?.version ?? told?.version ?? 1, message: "No date moved, so the published version is unchanged and the merchant was not notified." });
        // What the server checks too: a schedule with nothing in it is not sent.
        const shown = await getTimeline(b);
        if (shown && scheduleIsEmpty(shown)) {
            return delay({ code: "timeline_empty", message: "Nothing is scheduled yet. Add the plan's lines under What was sold before publishing." });
        }
        store.publishedTimelines = { ...(store.publishedTimelines ?? {}), [b]: { version: 1, at: nowIso() } };
        save();
        publishedListeners.forEach((cb) => cb(b, 1));
        return delay<PublishResult>({ published: true, datesMoved: shown?.diff.length ?? 0, version: 1 });
    },
    getTimeline(b: string): Promise<TimelinePayload | null> {
        const e = withCatalogue(b) ?? DEMO_ENGAGEMENT;
        const start = e.startedAt ? new Date(e.startedAt) : null;
        if (!start) {
            return delay({
                business: { id: b, domain: `${b}.example` },
                changePending: false, published: null, computed: null,
                problems: ["No start date on the engagement, so no date in the term can be computed yet."],
                conflicts: [], diff: [], datesMoved: 0, canPublish: false,
            });
        }
        const iso = (d: Date) => d.toISOString().slice(0, 10);
        const at = (k: number) => { const d = new Date(start); d.setMonth(d.getMonth() + k); return d; };
        // fixture-only: the demo business has a published version, the
        // schedule its client page shows, so the Timeline tab, the staff
        // Overview and the merchant's Work state the same report dates.
        const told = b === DEMO_CLIENT.businessId ? DEMO_PAGE.schedule ?? null : null;
        const months: ScheduleMonth[] = Array.from({ length: e.termMonths }, (_, i) => {
            const k = i + 1, s0 = at(i), s1 = at(k);
            s1.setDate(s1.getDate() - 1);
            const shown = told?.months.find((m) => m.termMonth === k);
            const report = shown?.reportDate ?? iso(s1);
            return {
                term_month: k, window: shown?.window ?? `${s0.getDate()} to ${s1.getDate()} ${s1.toLocaleDateString("en-GB", { month: "short", year: "numeric" })}`,
                start: iso(s0), end: iso(s1),
                pages_this_month: k === 1 ? 8 : 2, pages_cumulative: k === 1 ? 8 : 8 + (k - 1) * 2,
                report_date: report, report_date_text: report,
                lands: e.deliverables.filter((d) => d.months.includes(k)).map((d) => d.label),
            };
        });
        const computed: Schedule = {
            client: e.client, reference: e.ref, plan_label: e.planName,
            term_months: e.termMonths, term_months_source: "contract",
            total_units: e.pageUnits, total_units_source: "contract",
            trigger_date: iso(start), trigger_date_text: iso(start),
            clock_start: iso(start), clock_start_text: iso(start),
            handover: iso(at(1)), handover_text: iso(at(1)),
            term_end: iso(at(e.termMonths)), term_end_text: iso(at(e.termMonths)),
            keywords_by: iso(start), keywords_by_text: iso(start),
            // What the contract holds, so the blocker clears once the keywords are saved (Angel, 15 Oct).
            priority_keywords: [...(e.tracking?.priority_keywords ?? [])],
            holidays: [], months, version: "v1.0", prepared_by: DEMO_STAFF.name,
            issued: iso(start),
            conflicts: keywordConflicts(e),
        };
        // The version the client page was told: its October report moved from
        // the 17th to the 16th (DEMO_PAGE.schedule.changed).
        const done = store.publishedTimelines?.[b];
        const published: PublishedTimeline | null = told
            ? {
                id: `pub_${b}_v${told.version}`, version: told.version, datesMoved: 1,
                diff: [{ scope: "month", termMonth: 2, field: "report_date", label: "Report", from: "2026-10-17", to: "2026-10-16" }],
                publishedBy: DEMO_STAFF.name, publishedAt: told.publishedAt, schedule: computed,
            }
            : done
                ? { id: `pub_${b}_v${done.version}`, version: done.version, datesMoved: 0, diff: [], publishedBy: DEMO_STAFF.name, publishedAt: done.at, schedule: computed }
                : null;
        return delay({
            business: { id: b, domain: `${b}.example` },
            changePending: !published, published, computed,
            problems: [], conflicts: computed.conflicts,
            // Never published: the first publish sends every date (the server's diff from nothing).
            // Open only with nothing in the way; an empty schedule is refused by timelinePublishable.
            ...(published ? { diff: [], datesMoved: 0 } : (() => { const d = firstPublishDiff(computed); return { diff: d, datesMoved: d.length }; })()),
            canPublish: !published && computed.conflicts.length === 0,
        });
    },
    seedEngagement(b: string, e: Omit<Engagement, "changeLog">) {
        // A quote read from a PDF carries no lines (page units 0): the server writes them from its catalogue.
        if (e.deliverables.length === 0 && e.pageUnits === 0) {
            const lines = catalogueLines(e.termMonths);
            e = { ...e, deliverables: lines, pageUnits: cataloguePages(lines) };
        }
        store.engagements[b] = { ...e, changeLog: [{ at: nowIso(), by: DEMO_STAFF.name, summary: `Seeded from quotation ${e.ref}` }] };
        return delay(store.engagements[b]);
    },
    updateEngagement(b: string, patch: EngagementPatch, by: string, summary: string) {
        // Refuse rather than conjure one (#3711 AC2). Cloning the demo here
        // turned "edit a plan that does not exist" into "create a plan nobody
        // quoted", silently, on whichever business happened to be open.
        const existing = store.engagements[b];
        if (!existing) {
            return Promise.reject(new Error(`No engagement on ${b} to update. Seed one from a quotation first.`));
        }
        const e = existing;
        const { expectedUpdatedAt, ...fields } = patch;
        // H27: an edit made from an older read is refused, as the server does.
        if (expectedUpdatedAt && e.updatedAt && e.updatedAt !== expectedUpdatedAt) {
            const message = "Someone changed this contract a moment ago. Reload it and add the line again.";
            return Promise.reject(Object.assign(new Error(message), { response: { status: 409, data: { code: 409, errors: ["engagement_changed"], message } } }));
        }
        // Stored as sent: a line with no covers stays without them, as a row
        // from before #4814 does, and every reader authors or defaults it.
        Object.assign(e, fields);
        e.updatedAt = nowIso();
        e.changeLog.unshift({ at: nowIso(), by, summary });
        store.engagements[b] = e;
        notify("client", b, "Your plan was updated", summary);
        return delay(e);
    },
    uploadFile: (file: File) => {
        const uploaded: UploadedFile = { fileKey: `mock/${file.name}`, url: URL.createObjectURL(file), name: file.name, size: file.size };
        mockUploads.set(uploaded.fileKey, { name: uploaded.name, url: uploaded.url });
        return delay<UploadedFile>(uploaded, 200);
    },
    // Not `delay(undefined)`: its JSON round trip throws on undefined inside the timer, so the upload never settled.
    uploadReport: (): Promise<void> => delay(null, 200).then(() => undefined),
    /** A labelled placeholder image, so the preview opens something. */
    activityAttachment: (path: string) => {
        const name = path.slice(ATTACHMENT_PATH.length).replace(/^demo-/, "");
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="400"><rect width="640" height="400" fill="#F5F5F7"/><text x="320" y="210" font-family="Inter, sans-serif" font-size="28" text-anchor="middle" fill="#6B7280">Sample screenshot: ${name}</text></svg>`;
        // Not through `delay`, whose JSON round trip would turn the Blob into `{}`.
        return new Promise<Blob>((r) => window.setTimeout(() => r(new Blob([svg], { type: "image/svg+xml" })), 400));
    },
    createClientUser: (input: { name: string; email: string }) =>
        delay<CreateClientUserResult>({ ok: true, email: input.email }, 200),
    listClientNotes: (b: string) => delay((store.notes?.[b] ?? []).slice(0, 3)),
    postClientNote(b: string, body: string) {
        // The server records the caller's name, and the merchant reads it on Home.
        const note: ClientNote = { id: nextId("note"), date: new Date().toISOString(), body, author: DEMO_STAFF.name };
        store.notes ??= {};
        store.notes[b] = [note, ...(store.notes[b] ?? [])];
        save();
        return delay(note, 100);
    },
    listTaskApprovals: (b: string) => delay(store.approvals[b] ?? []),
    setTaskApproval(b: string, taskId: string, requirement: ApprovalRequirement, showToClient = true, why?: ApprovalOverrideReason) {
        const list = (store.approvals[b] ??= []).filter((a) => a.taskId !== taskId);
        const o: ApprovalOverride = {
            taskId, requirement, source: "staff", showToClient,
            ...(why ? { setBy: why.name, setAt: new Date().toISOString(), reason: why.reason, whereClientSaidYes: why.whereClientSaidYes } : {}),
        };
        list.push(o); store.approvals[b] = list;
        save();
        return delay(o, 100);
    },
    listPacks: (b: string) => delay(store.packs.filter((p) => p.businessId === b).map(withLateness)),
    createPack(b: string, draft: PackDraft) {
        const packId = nextId("pack");
        const items: PackItem[] = draft.items.map((it) => ({
            ...it, id: nextId("item"), packId, version: 1,
            decision: "pending", decidedAt: null, decidedBy: null, answer: null, fileName: null, waitingOn: null,
        }));
        // Decision blocks were authored against temporary ids (the taskId); rebind them.
        const blocks = draft.blocks.map((bl) => bl.type === "decision"
            ? { ...bl, itemId: items.find((i) => i.taskId === bl.itemId)?.id ?? bl.itemId }
            : bl);
        const pack: Pack = { id: packId, businessId: b, ref: refFor(b), title: draft.title, intro: draft.intro, status: "draft", blocks, items, preparedBy: DEMO_STAFF.name, createdAt: nowIso(), publishedAt: null, submittedAt: null };
        store.packs.unshift(pack);
        return delay(pack);
    },
    updatePack(id: string, draft: Partial<PackDraft>, by: string) {
        const pack = store.packs.find((p) => p.id === id);
        if (!pack) return Promise.reject(new Error("pack not found"));
        if (draft.title !== undefined) pack.title = draft.title;
        if (draft.intro !== undefined) pack.intro = draft.intro;
        if (draft.blocks) pack.blocks = draft.blocks;
        if (draft.items) {
            for (const d of draft.items) {
                const item = pack.items.find((i) => i.taskId === d.taskId);
                if (!item) continue;
                const changed = pack.status !== "draft" && item.proposed !== d.proposed;
                Object.assign(item, { title: d.title, why: d.why, kind: d.kind, tags: d.tags, current: d.current, proposed: d.proposed, outline: d.outline, options: d.options });
                if (changed) {
                    item.version += 1;
                    if (item.decision !== "approved") { item.decision = "pending"; item.waitingOn = "client"; }
                    threadFor(item, pack.businessId).messages.push({ id: nextId("m"), kind: "version", authorKind: "staff", authorName: by, body: `${by} updated the wording. Version ${item.version - 1} to version ${item.version}.`, fileName: null, suggestedEdit: null, at: nowIso() });
                    notify("client", pack.businessId, "Wording updated", `${by} revised "${item.title}". Have another look.`, item.id, pack.id);
                }
            }
        }
        return delay(pack);
    },
    publishPack(id: string) {
        const pack = store.packs.find((p) => p.id === id);
        if (!pack) return Promise.reject(new Error("pack not found"));
        pack.status = "published"; pack.publishedAt = nowIso();
        for (const item of pack.items) { item.waitingOn = "client"; threadFor(item, pack.businessId); }
        notify("client", pack.businessId, `${pack.items.length} decision${pack.items.length === 1 ? "" : "s"} waiting for you`, `${pack.preparedBy} sent "${pack.title}".`, null, pack.id);
        return delay(pack);
    },
    listCallRequests: (b: string) => delay(store.calls.filter((c) => c.businessId === b)),
    updateCallRequest(id: string, status: CallRequest["status"]) {
        const c = store.calls.find((x) => x.id === id);
        if (!c) return Promise.reject(new Error("not found"));
        c.status = status;
        // Links the call on Work's "Your requests" (BE handoff B9), where it now shows as Scheduled.
        if (status === "scheduled") notify("client", c.businessId, "Call confirmed", `${DEMO_STAFF.name} will call you. Check your email for the time.`, c.itemId, null, `/oura/work?request=${c.id}`);
        return delay(c);
    },
    acceptSuggestedEdit(itemId: string, messageId: string, by: string) {
        const f = findItem(itemId); if (!f) return Promise.reject(new Error("item not found"));
        const t = threadFor(f.item, f.pack.businessId);
        const m = t.messages.find((x) => x.id === messageId);
        if (!m?.suggestedEdit) return Promise.reject(new Error("no edit"));
        m.suggestedEdit.accepted = true;
        f.item.proposed = m.suggestedEdit.after;
        f.item.version += 1;
        f.item.decision = "approved"; f.item.decidedAt = nowIso(); f.item.decidedBy = m.authorName; f.item.waitingOn = null;
        t.messages.push({ id: nextId("m"), kind: "decision", authorKind: "staff", authorName: by, body: `${by} accepted ${m.authorName}'s wording. Recorded as approved, version ${f.item.version}.`, fileName: null, suggestedEdit: null, at: nowIso() });
        t.status = "resolved";
        notify("client", f.pack.businessId, "Your wording was accepted", `"${f.item.title}" is approved with your edit.`, f.item.id, f.pack.id);
        return delay(f.item);
    },

    getMyBusiness: () => delay<MyBusiness>({ id: DEMO_CLIENT.businessId, name: "Nyonya Table", city: "Bukit Bintang" }),
    // The docs dev mode's "slow" reaches the client page too, since it carries `documents`.
    getClientPage: (): Promise<ClientPagePayload | null> => delay(demoClientPage(DEMO_CLIENT.name), docsMockDelayMs()),
    /**
     * The staff read of the same page. Only the sample business has page data
     * in the mock, so any other id answers as the 404 path does.
     */
    getClientPageFor: (b: string): Promise<ClientPagePayload | null> =>
        delay(b === DEMO_CLIENT.businessId ? { ...demoClientPage(DEMO_STAFF.name), previewedByStaff: true } : null, docsMockDelayMs()),
    decideItem(itemId: string, input: DecisionInput) {
        const f = findItem(itemId); if (!f) return Promise.reject(new Error("item not found"));
        const { pack, item } = f;
        item.decision = input.decision; item.decidedAt = nowIso(); item.decidedBy = input.by;
        if (input.answer !== undefined) item.answer = input.answer;
        // `fileKey` wins over the name, as on the server. The mock has no
        // presign step, so the stored name is the key's last segment.
        if (input.fileKey !== undefined) item.fileName = input.fileKey ? input.fileKey.split("/").pop() ?? input.fileKey : null;
        else if (input.fileName !== undefined) item.fileName = input.fileName;
        item.waitingOn = input.decision === "changes_requested" ? "staff" : null;
        // The merchant taking back their own answer is not late on it: the
        // reply-by date belonged to the first asking. With none, the reopened
        // ask says when it was asked and claims no deadline.
        if (input.decision === "pending") item.replyBy = null;
        const t = threadFor(item, pack.businessId);
        const label = { approved: "approved this", answered: `answered: ${item.answer ?? item.fileName ?? ""}`, changes_requested: "requested changes", deferred: "deferred this", pending: "reopened this" }[input.decision];
        t.messages.push({ id: nextId("m"), kind: "decision", authorKind: "client", authorName: input.by, body: `${input.by} ${label}.`, fileName: null, suggestedEdit: null, at: nowIso() });
        if (input.comment) t.messages.push({ id: nextId("m"), kind: "message", authorKind: "client", authorName: input.by, body: input.comment, fileName: null, suggestedEdit: null, at: nowIso() });
        if (input.decision === "approved" || input.decision === "answered") t.status = "resolved";
        if (pack.items.every((i) => i.decision !== "pending")) { pack.status = "submitted"; pack.submittedAt = nowIso(); }
        notify("staff", pack.businessId, `${input.by} ${label}`, item.title, item.id, pack.id);
        return delay(item);
    },
    requestCall(b: string, input: { itemId: string | null; requestedBy: string; preference: string; note: string }) {
        const c: CallRequest = { id: nextId("call"), businessId: b, itemId: input.itemId, requestedBy: input.requestedBy, preference: input.preference, note: input.note, status: "open", at: nowIso() };
        store.calls.unshift(c);
        const f = input.itemId ? findItem(input.itemId) : null;
        if (f) threadFor(f.item, b).messages.push({ id: nextId("m"), kind: "call_request", authorKind: "client", authorName: input.requestedBy, body: `${input.requestedBy} asked for a call${input.preference ? `, ${input.preference}` : ""}.${input.note ? ` "${input.note}"` : ""}`, fileName: null, suggestedEdit: null, at: nowIso() });
        // Links the call itself, so the bell opens it on the Requests card (BE handoff B7, step 4).
        notify("staff", b, `${input.requestedBy} asked for a call`, f ? `On "${f.item.title}". ${input.preference}` : input.preference, input.itemId, null, `/oura/businesses/${b}/reviews?request=${c.id}`);
        return delay(c);
    },
    // The merchant's own business, as the server would take it from the token.
    requestChange(input: { note: string; requestedBy: string; fileKeys?: string[] }) {
        return changeCall(() => {
            const b = DEMO_CLIENT.businessId;
            const note = input.note.trim();
            if (!note) throw changeRefusal("note_required", 400);
            if (note.length > 2000) throw changeRefusal("note_too_long", 400);
            const files = (input.fileKeys ?? []).map((fileKey) => {
                // The mock upload answers "mock/<name>"; the server's keys sit under oura/uploads/{business}/.
                if (!fileKey.startsWith(`oura/uploads/${b}/`) && !fileKey.startsWith("mock/")) throw changeRefusal("file_not_yours", 400);
                const known = mockUploads.get(fileKey);
                return { fileKey, name: known?.name ?? fileKey.split("/").pop() ?? fileKey, url: known?.url ?? "" };
            });
            const change: ChangeRequest = {
                id: nextId("change"), businessId: b, requestedBy: input.requestedBy, requesterEmail: DEMO_CLIENT_EMAIL, note, files,
                status: "open", reply: null, smallChange: null, at: nowIso(), doneAt: null, doneBy: null,
            };
            store.changes = [change, ...changesOf()];
            const attached = files.length ? ` Attached: ${files.map((f) => f.name).join(", ")}.` : "";
            notify("staff", b, `${input.requestedBy} asked for a change`, `${note.slice(0, 200)}${attached}`, null, null, `/oura/businesses/${b}/reviews?request=${change.id}`);
            return withFileUrls(change);
        });
    },
    /** Newest first, as route 15 returns them. */
    listChangeRequests: (b: string) =>
        changeCall(() => changesOf().filter((c) => c.businessId === b).sort((x, y) => Date.parse(y.at) - Date.parse(x.at)).map(withFileUrls)),
    /** The merchant's own calls, with the question each is on (route 19). */
    listMyCallRequests: () =>
        changeCall(() =>
            store.calls
                .filter((c) => c.businessId === DEMO_CLIENT.businessId)
                .map((c) => ({ id: c.id, requestedBy: c.requestedBy, about: c.itemId ? findItem(c.itemId)?.item.title ?? null : null, preference: c.preference, note: c.note, status: c.status, at: c.at })),
        ),
    /** The merchant's own business, newest first, without the staff-only fields (route 18). */
    listMyChangeRequests: () =>
        changeCall(() =>
            changesOf()
                .filter((c) => c.businessId === DEMO_CLIENT.businessId)
                .sort((x, y) => Date.parse(y.at) - Date.parse(x.at))
                .map((c) => toMyChangeRequest(withFileUrls(c))),
        ),
    replyToChangeRequest(id: string, body: string, fileKeys: string[] = []) {
        return changeCall(() => {
            const c = changesOf().find((x) => x.id === id);
            if (!c) throw new ChangeRequestError("That request is gone.", { status: 404 });
            const text = body.trim();
            if (!text) throw changeRefusal("reply_required", 400);
            const files = fileKeys.flatMap((k) => {
                const up = mockUploads.get(k);
                return up ? [{ fileKey: k, name: up.name, url: up.url }] : [];
            });
            c.reply = { body: text, by: DEMO_STAFF.name, at: nowIso(), ...(files.length > 0 ? { files } : {}) };
            if (c.status !== "done") c.status = "answered";
            notify("client", c.businessId, `${DEMO_STAFF.name} replied to your request`, text.slice(0, 200), null, null, `/oura/work?request=${c.id}`);
            return withFileUrls(c);
        });
    },
    updateChangeRequest(id: string, patch: ChangeRequestPatchIn) {
        return changeCall(() => {
            const c = changesOf().find((x) => x.id === id);
            if (!c) throw new ChangeRequestError("That request is gone.", { status: 404 });
            if (patch.smallChange === true) {
                const month = myMonth();
                // Not changesOf() again: it rebuilds every object, and `c` would be left outside the store.
                const taken = (store.changes ?? []).find((x) => x.id !== c.id && x.businessId === c.businessId && x.smallChange?.month === month);
                if (taken && !patch.confirmOverLimit) throw changeRefusal("small_change_already_filed", 409, taken.id);
                const e = store.engagements[c.businessId];
                c.smallChange = { month, termMonth: e ? monthOf(e) : null, filedBy: DEMO_STAFF.name, filedAt: nowIso() };
            } else if (patch.smallChange === false) {
                c.smallChange = null;
            }
            if (patch.status) {
                c.status = patch.status;
                c.doneAt = patch.status === "done" ? nowIso() : null;
                c.doneBy = patch.status === "done" ? DEMO_STAFF.name : null;
            }
            return withFileUrls(c);
        });
    },

    listThreads: (b: string) => delay(store.threads.filter((t) => t.businessId === b)),
    postMessage(threadId: string, input: MessageInput) {
        const t = store.threads.find((x) => x.id === threadId);
        if (!t) return Promise.reject(new Error("thread not found"));
        const f = findItem(t.itemId);
        t.messages.push({ id: nextId("m"), kind: input.suggestedEdit ? "suggested_edit" : "message", authorKind: input.authorKind, authorName: input.authorName, body: input.body, // The mock shows the key, since it has no presign step; the real read side
            // returns a name alongside the URL.
            fileName: input.fileKey ?? null, suggestedEdit: input.suggestedEdit ? { ...input.suggestedEdit, accepted: false } : null, at: nowIso() });
        if (t.status === "resolved" && input.authorKind === "client") t.status = "open";
        if (f) {
            f.item.waitingOn = input.authorKind === "client" ? "staff" : "client";
            const other: Audience = input.authorKind === "client" ? "staff" : "client";
            notify(other, t.businessId, input.suggestedEdit ? `${input.authorName} suggested an edit` : `${input.authorName} replied`, input.suggestedEdit ? f.item.title : `On "${f.item.title}": ${input.body.slice(0, 80)}`, f.item.id, f.pack.id);
        }
        return delay(t);
    },
    resolveThread(threadId: string) {
        const t = store.threads.find((x) => x.id === threadId);
        if (!t) return Promise.reject(new Error("thread not found"));
        t.status = "resolved";
        return delay(t);
    },
    listNotifications: (audience: Audience, b: string) => delay(store.notifications.filter((n) => n.audience === audience && n.businessId === b)),
    markRead(id: string) {
        const n = store.notifications.find((x) => x.id === id);
        if (!n) return Promise.reject(new Error("not found"));
        n.read = true;
        return delay(n, 60);
    },
};

/**
 * The 12 months of the demo term as the timeline engine writes them: window,
 * report date, what lands. Declared before `DEMO_PAGE`, which reads it.
 * The AI visibility check lands every month: every monthly report carries it.
 * `pagesThisMonth` on every month:
 * fixture-only until ourteam-backend handoff: Upcoming dated milestones for the merchant
 */
const DEMO_MONTHS: MerchantScheduleMonth[] = [
    { termMonth: 1, window: "18 Aug to 17 Sep 2026", reportDate: "2026-09-16", pagesThisMonth: 10, lands: ["Site health sweep", "Your 50 questions set", "10 pages live", "AI visibility check"] },
    { termMonth: 2, window: "18 Sep to 17 Oct 2026", reportDate: "2026-10-16", pagesThisMonth: 3, lands: ["3 new pages, starting with your signature page", "Site health sweep", "Your scoreboard", "AI visibility check"] },
    { termMonth: 3, window: "18 Oct to 17 Nov 2026", reportDate: "2026-11-16", pagesThisMonth: 3, lands: ["3 new pages", "Answers for your FAQ page", "AI visibility check"] },
    { termMonth: 4, window: "18 Nov to 17 Dec 2026", reportDate: "2026-12-16", pagesThisMonth: 3, lands: ["3 new pages", "Your contact details brought up to date everywhere", "AI visibility check"] },
    { termMonth: 5, window: "18 Dec 2026 to 17 Jan 2027", reportDate: "2027-01-16", pagesThisMonth: 3, lands: ["The last 3 pages, all 22 live", "AI visibility check"] },
    ...(["Jan to 17 Feb", "Feb to 17 Mar", "Mar to 17 Apr", "Apr to 17 May", "May to 17 Jun", "Jun to 17 Jul", "Jul to 17 Aug"] as const).map((span, k): MerchantScheduleMonth => {
        const termMonth = k + 6;
        const reportMonth = String(k + 2).padStart(2, "0");
        return {
            termMonth,
            window: `18 ${span} 2027`,
            reportDate: `2027-${reportMonth}-16`,
            pagesThisMonth: 0,
            lands: [
                "Site health sweep",
                "Competitor watch",
                "Your monthly snapshot",
                "AI visibility check",
                ...(termMonth === 6 || termMonth === 12 ? ["Term review"] : []),
            ],
        };
    }),
];

/**
 * What the server will add to a plan line: the asks it waits on and a line
 * saying what it is.
 * fixture-only until ourteam-backend handoff: Plan line links and descriptions
 */
const DEMO_PLAN_EXTRAS: Record<string, { itemIds: string[]; description: string }> = {
    G1: { itemIds: [], description: "3 pages this month: your signature page, family and group bookings, and catering. The first comes to you on Fri 2 Oct." },
    G2: { itemIds: [], description: "Broken links fixed, images compressed and page tags tidied across 32 pages." },
    G3: { itemIds: [], description: "Your 50 questions, set on 18 Aug. Every check asks the same ones, so the numbers compare." },
    G4: { itemIds: [], description: "Who AI names instead of you, at every check." },
    G5: { itemIds: ["item_demo_hours"], description: "Under 30 minutes of work. This month: your opening hours on the contact page." },
    G6: { itemIds: [], description: "1 page with your numbers, sent with each report." },
};

/** Fixture progress for the demo merchant. The BE builds this from the plan,
 *  the activity record and the connectors; the FE only renders it. */
const DEMO_PAGE: Omit<ClientPagePayload, "business" | "viewer" | "packs" | "threads" | "engagement" | "plan" | "weekNote"> = {
    preparedBy: DEMO_STAFF.name,
    // fixture-only until ourteam-backend handoff: Client email reply-to
    contactEmail: DEMO_CONTACT_EMAIL,
    updatedAt: "2026-09-27",
    lifecycle: { state: "active", since: "2026-08-18" },
    stats: {
        // 15: the questions the Visibility fixture marks covered (nyonya-visibility.ts), so the sample agrees with itself.
        coverage: { answered: 15, total: 50, score: null, measuredAt: "2026-09-16", previous: { answered: 11, total: 50, score: null, measuredAt: "2026-08-18" } },
        coverageAbsent: null,
        pages: { published: 10, planned: 12 },
        search: {
            windowDays: 28, clicks: 414, clicksPrevious: 358, impressions: 18900, impressionsPrevious: 15200, measuredAt: "2026-09-27",
            // fixture-only until ourteam-backend handoff: Top searches by clicks on the client page (optional)
            topQueries: [
                { query: "nyonya table", clicks: 96, branded: true },
                { query: "peranakan restaurant bukit timah", clicks: 42, branded: false },
                { query: "nyonya food bukit timah", clicks: 31, branded: false },
                { query: "ayam buah keluak", clicks: 18, branded: false },
                { query: "peranakan restaurant near me", clicks: 14, branded: false },
            ],
        },
        appearingFor: ["ayam buah keluak", "private dining bukit timah", "peranakan set lunch", "laksa bukit timah", "group dinner bukit timah"],
        appearingForAbsent: null,
        // The next monthly report's date: the AI check is monthly and every report carries it.
        // fixture-only until ourteam-backend handoff: Monthly AI check
        aiCheck: { band: null, measuredAt: null, bandAbsent: "no_app_measurement", nextAt: "2026-10-16" },
        googleProfile: { status: "not_claimed", note: "Your Google Business Profile is not connected to ourteam, so we cannot check it from here." },
    },
    subscription: { ref: "OT-2026-NYO-001", name: "Annual plan", startedAt: "2026-08-18", termMonths: 12, pagesPublished: 10, pageUnits: 12, outlets: 1 },
    // fixture-only until ourteam-backend handoff: Upcoming dated milestones for the merchant
    upcoming: [
        { date: "2026-10-02", label: "Your signature page comes to you", kind: "handover", was: null, reason: null, itemId: null },
        { date: "2026-10-14", label: "New pages go live", kind: "golive", was: "2026-10-09", reason: "Waiting on your prices.", itemId: "item_demo_prices" },
        { date: "2026-10-16", label: "Your monthly report and AI check", kind: "report", was: null, reason: null, itemId: null },
        { date: "2026-11-16", label: "Your monthly report and AI check", kind: "report", was: null, reason: null, itemId: null },
    ],
    // fixture-only until ourteam-backend handoff: Published and upcoming page list
    pages: {
        published: [
            { title: "Homepage", url: "https://nyonyatable.com/", publishedAt: "2026-09-02" },
            { title: "Prices", url: "https://nyonyatable.com/prices", publishedAt: "2026-09-02" },
            { title: "Bukit Bintang location", url: "https://nyonyatable.com/locations/bukit-bintang", publishedAt: "2026-09-04" },
            { title: "FAQs", url: "https://nyonyatable.com/faqs", publishedAt: "2026-09-04" },
            { title: "Reservations", url: "https://nyonyatable.com/booking", publishedAt: "2026-09-08" },
            { title: "About us", url: "https://nyonyatable.com/about", publishedAt: "2026-09-08" },
            { title: "Private dining", url: "https://nyonyatable.com/private-dining", publishedAt: "2026-09-11" },
            { title: "Catering", url: "https://nyonyatable.com/catering", publishedAt: "2026-09-11" },
            { title: "Gift vouchers", url: "https://nyonyatable.com/gift-vouchers", publishedAt: "2026-09-15" },
            { title: "Contact", url: "https://nyonyatable.com/contact", publishedAt: "2026-09-15" },
        ],
        upcoming: [
            { title: "Your signature page", dueAt: "2026-10-14" },
            { title: "Family and group bookings", dueAt: "2026-10-14" },
            { title: "Catering prices", dueAt: "2026-10-14" },
        ],
    },
    // `kind`, `why` and evidence `url` on every row:
    // fixture-only until ourteam-backend handoff: Activity kind, why it matters, evidence links
    activity: [
        { id: "a1", at: "2026-09-18", body: "Compressed images on 10 pages", meta: "Homepage: 6.1 MB to 1.4 MB", kind: "site", why: "Pages open faster on phones, and Google counts speed.", evidence: [{ label: "Before", url: `${ATTACHMENT_PATH}demo-before` }, { label: "After", url: `${ATTACHMENT_PATH}demo-after` }] },
        { id: "a2", at: "2026-09-16", body: "Drafted your signature page", meta: "Comes to you Fri 2 Oct", kind: "page", why: "“Ayam buah keluak” is asked 3 ways, and no page answers it yet." },
        { id: "a3", at: "2026-09-11", body: "Fixed 3 broken links, 2 dead pages", meta: "Rechecked 16 Sep. All working.", kind: "site", why: "A dead link wastes a visit, from a person or from Google." },
        { id: "a4", at: "2026-09-11", body: "Added canonical tags to 18 pages", meta: "Site health 68 to 75 at the 16 Sep check", kind: "site", why: "Each page now has 1 address, so its strength is not split." },
        { id: "a5", at: "2026-08-29", body: "Submitted your sitemap to Google", meta: "Search Console now reads all 32 pages", kind: "google", why: "Google finds new pages sooner when the sitemap lists them." },
        { id: "a6", at: "2026-08-18", body: "Set your 50 questions", meta: "Every check asks the same ones, so the numbers compare", kind: "report", why: null },
    ],
    decisions: [
        { id: "d_demo_vouchers", title: "A page for gift vouchers, in time for the holidays", decision: "deferred", decidedAt: "2026-08-28", workStatus: "Not started" },
    ],
    // `kind`, `sections` and `termMonth`:
    // fixture-only until ourteam-backend handoff: Report kind and sections, and Report tied to its term month
    reports: [
        { id: "r_sep", title: "Monthly report, September", date: "2026-09-16", url: null, urlAbsent: null, pages: 6, kind: "snapshot", termMonth: 1, summary: "Named in 23 of 50. 414 clicks from Google. 4 pieces of work done.", sections: ["Summary", "Who came to you", "How AI names you", "Latest actions", "Next month"] },
        { id: "r0", title: "Proposal and quotation", date: "2026-08-11", url: null, urlAbsent: "not_uploaded", pages: null, kind: "document", termMonth: null, summary: "What you bought: the Annual plan, 22 pages across the term.", sections: null },
        { id: "r_build", title: "Build month report", date: "2026-08-18", url: null, urlAbsent: null, pages: 8, kind: "upload", termMonth: null, summary: "Your 50 questions are set. First check: named in 21 of 50.", sections: ["Your 50 questions", "Site health", "The plan"] },
        { id: "r_assess", title: "AI visibility report", date: "2026-07-07", url: null, urlAbsent: null, pages: 5, kind: "assessment", termMonth: null, summary: "Named in 19 of 50, on the questions used then.", sections: ["Your score", "Who AI names instead", "What's stopping it", "What to do", "Check it yourself"] },
    ],
    /** A published schedule with a change entry, so both halves of T-7 are
     *  reachable on fixtures: the schedule itself, and what moved since the
     *  merchant was last told. */
    schedule: {
        version: 4,
        publishedAt: "2026-09-14T03:12:00Z",
        clockStart: "2026-08-18",
        termEnd: "2027-08-17",
        currentMonth: 2,
        months: DEMO_MONTHS,
        changed: {
            datesMoved: 1,
            summary: "Your October report now comes Fri 16 Oct, a day earlier.",
            moves: [{ label: "Report", from: "2026-10-17", to: "2026-10-16" }],
        },
    },
    noDecisionNeeded: [
        { label: "Add a robots.txt file to your site", status: "done" },
        { label: "Fix 3 broken links and 2 pages nobody can reach", status: "done" },
        { label: "Compress images on 10 pages", status: "done" },
        { label: "Set up Search Console and clear indexing errors", status: "in_progress" },
        { label: "Tag each outlet's address, hours and prices for AI", status: "not_started" },
        { label: "Link service pages to price and booking pages", status: "not_started" },
    ],
};

/** The demo merchant's plan as sold. Seeded into the store once; staff edit the store copy. */
const DEMO_ENGAGEMENT: Omit<Engagement, "changeLog"> & { changeLog: EngagementChange[] } = {
    ref: "OT-2026-NYO-001", client: "Nyonya Table", planName: "Annual plan", termMonths: 12, pageUnits: 12, outlets: 1, skipPublicHolidays: true,
    startedAt: "2026-08-18",
    deliverables: [
        { code: "A3", label: "Let AI read your site", months: [1], quantity: null, cadence: "once", note: null, covers: ["robots_missing"], reported_by_hand: false },
        { code: "S3", label: "Technical crawl, broken pages and dead links fixed", months: [1], quantity: null, cadence: "once", note: null, covers: ["broken_4xx", "broken_outbound", "redirect_chains"], reported_by_hand: false },
        { code: "A1", label: "Your 50 questions locked and scored on day one", months: [1], quantity: null, cadence: "once", note: null, covers: [], reported_by_hand: true },
        { code: "S5", label: "Search Console set up, baseline recorded", months: [1], quantity: null, cadence: "once", note: null, covers: [], reported_by_hand: true },
        { code: "S4", label: "Your name, address and contact corrected across the web", months: [1], quantity: null, cadence: "once", note: null, covers: [], reported_by_hand: true },
        { code: "S6", label: "Pages linked together", months: [1], quantity: null, cadence: "once", note: null, covers: ["orphan_pages"], reported_by_hand: false },
        { code: "S7", label: "Pages load fast", months: [1], quantity: "10 pages", cadence: "once", note: null, covers: ["cwv"], reported_by_hand: false },
        // B1 and G1 carry the plan the merchant sees (DEMO_MONTHS, the page list): 10 pages in month 1, then 3 a month to 22 by month 5.
        // As on a real quotation, month 1's pages are B1's own count and pageUnits is G1's 12 (YW, 30 Sep 2026).
        // Any page, in either line, can be a new one or an existing one improved; which is agreed with the client during the work.
        { code: "B1", label: "Pages written and published", months: [1], quantity: "10 pages, 2 of them Signature", cadence: "once", note: null, covers: [], reported_by_hand: true },
        { code: "S1", label: "Existing pages labelled", months: [1], quantity: "10 pages", cadence: "once", note: null, covers: ["missing_meta", "dup_titles", "missing_alt"], reported_by_hand: false },
        { code: "A2", label: "Business and page facts tagged for AI", months: [1], quantity: null, cadence: "once", note: null, covers: ["no_schema"], reported_by_hand: false },
        { code: "S2", label: "Google Business Profile, every outlet", months: [1], quantity: "1 outlet", cadence: "once", note: "Google verifies on its own clock, 1 to 2 weeks", covers: [], reported_by_hand: true },
        { code: "A4", label: "Handover Scorecard and call", months: [1], quantity: null, cadence: "once", note: null, covers: [], reported_by_hand: true },
        { code: "G1", label: "New pages this month", months: [2, 3, 4, 5], quantity: "3 a month", cadence: "monthly", note: "12 across the term, each a new page or an existing one improved", covers: [], reported_by_hand: true },
        { code: "G2", label: "Site health sweep", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], quantity: null, cadence: "monthly", note: null, covers: ["broken_4xx", "redirect_chains", "canonicals", "thin_content"], reported_by_hand: false },
        { code: "G3", label: "Your scoreboard", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], quantity: null, cadence: "monthly", note: null, covers: [], reported_by_hand: true },
        { code: "G4", label: "Competitor watch", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], quantity: "5 rivals", cadence: "weekly, reported monthly", note: null, covers: [], reported_by_hand: true },
        { code: "G5", label: "1 small change", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], quantity: "1", cadence: "monthly", note: "Under 30 minutes. Anything bigger is quoted first", covers: [], reported_by_hand: true },
        { code: "G6", label: "Your monthly snapshot", months: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], quantity: null, cadence: "monthly", note: null, covers: [], reported_by_hand: true },
        { code: "G7", label: "AI visibility check", months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], quantity: "each question asked 3 times", cadence: "monthly", note: "Reported as a band, never a point", covers: [], reported_by_hand: true },
        { code: "G8", label: "Term review", months: [6, 12], quantity: "60 minutes", cadence: "2 per term", note: null, covers: [], reported_by_hand: true },
    ],
    deviations: [],
    documents: [{ title: "Proposal and Quotation", file: "clients/nyonyatable/ourteam_Nyonya Table_Quotation_v1.0_11 Aug 2026.pdf" }],
    // The pair Oura picked from the first AI check (O4). The keywords stay short on purpose: the demo's one conflict.
    tracking: { signature: { visibility: "Where can I eat ayam buah keluak in Bukit Bintang?", coverage: "Private dining for 12 in Bukit Bintang" } },
    changeLog: [],
};
/** Work status per code for the demo. The BE derives this from tracker tasks joined by code. G5 is worked out in demoClientPage. */
const DEMO_STATUS: Record<string, ClientPlanStatus> = {
    A3: "done", S3: "done", A1: "done", S5: "done", S4: "done", S6: "done", S7: "done",
    B1: "in_progress", S1: "waiting_on_you", A2: "waiting_on_you", S2: "with_google", A4: "not_started",
    G1: "in_progress", G2: "done", G3: "done", G4: "in_progress", G6: "not_started", G7: "in_progress", G8: "not_started",
};


// ---------------------------------------------------------------------------
// Demo PDFs. The real reports are the PDFs the platform already renders
// (report_url on an assessment, presigned on read). Until the BE serves them
// here, a small one-page PDF is built in the browser so the viewer and the
// download button work end to end. DELETE with the rest of the mock.
// ---------------------------------------------------------------------------

const pdfCache = new Map<string, string>();

function pdfEscape(s: string): string {
    return s.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)").replace(/[^\x20-\x7e]/g, "");
}

function wrap(s: string, width: number): string[] {
    const out: string[] = []; let line = "";
    for (const w of s.split(/\s+/)) {
        if ((line + " " + w).trim().length > width) { out.push(line.trim()); line = w; } else line = `${line} ${w}`;
    }
    if (line.trim()) out.push(line.trim());
    return out;
}

function demoPdfUrl(r: ClientReportLink): string {
    const hit = pdfCache.get(r.id);
    if (hit) return hit;
    const lines: string[] = [];
    lines.push("BT /F1 22 Tf 56 770 Td (" + pdfEscape(r.title) + ") Tj ET");
    lines.push("BT /F1 11 Tf 56 748 Td (ourteam for Nyonya Table, " + pdfEscape(shortIso(r.date)) + ") Tj ET");
    let y = 700;
    for (const l of wrap(r.summary ?? "", 80)) { lines.push("BT /F1 12 Tf 56 " + y + " Td (" + pdfEscape(l) + ") Tj ET"); y -= 18; }
    y -= 12;
    const body = [
        "What this report covers",
        "1. Where you stand today, measured on the same 50 questions every month.",
        "2. What we published and fixed, with the proof.",
        "3. What needs your answer next, and what it unblocks.",
        "",
        "Nothing in this report comes from an AI answering from memory. Every number",
        "was measured on the day shown, on the questions your buyers actually ask.",
    ];
    for (const l of body) { lines.push("BT /F1 11 Tf 56 " + y + " Td (" + pdfEscape(l) + ") Tj ET"); y -= 16; }
    lines.push("BT /F1 9 Tf 56 60 Td (Demo document. The platform's rendered PDF replaces this.) Tj ET");
    const content = lines.join("\n");
    const objs = [
        "<< /Type /Catalog /Pages 2 0 R >>",
        "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
        "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>",
        "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
        "<< /Length " + content.length + " >>\nstream\n" + content + "\nendstream",
    ];
    let pdf = "%PDF-1.4\n"; const offsets: number[] = [];
    objs.forEach((o, i) => { offsets.push(pdf.length); pdf += `${i + 1} 0 obj\n${o}\nendobj\n`; });
    const xref = pdf.length;
    pdf += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n` + offsets.map((o) => String(o).padStart(10, "0") + " 00000 n \n").join("");
    pdf += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
    const url = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
    pdfCache.set(r.id, url);
    return url;
}

function shortIso(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// Runs last: the demo constants above are `const` and must be initialised first.
seedDemo();
seedChangeRequests();
// A doc shared with the client, or a client's answer in a doc, rings the bell the way the sync will.
onDocsNotice((n) => {
    notify(n.audience, n.businessId, n.title, n.body, null, null, n.link);
    save();
});

/** The Baseline Snapshot row: no file, drawn in the app from its scan (#4457). */
export function isInAppSnapshot(r: Pick<ClientReportLink, "kind" | "assessmentId">): boolean {
    return r.kind === "baseline_snapshot" && !!r.assessmentId;
}
